import uuid from "../helpers/uuid";
import StorageWrapper from "@/helpers/storage";
import { EventBus } from "@/helpers/eventBus";
import { ArtifactType } from "@/enums/ArtifactType";
import { SearchRequest, searchRequestToQueryString } from "@/types/search/SearchRequest";
import { ApiInterface, type SendInvitationResponse, type ConversationDelivery, type ProjectAccessVerdict, type ProjectAccessVerdictsResponse, type ApiRequestOptions, type MobileAppBannerEvent } from "./index.d";
import { auth } from "../auth.js";
import { jwtDecode } from "jwt-decode";
import { canReloadForOrgSwitch, isReloadPending } from "@/composables/useOrgSync";
import UnauthenticatedError from "@/types/errors/UnauthenticatedError";
import { isTransientAuthTokenError } from "@/types/errors/TransientAuthTokenError";
import { FileDetails } from "@/types/media/presignedUrlFileDetails";
import { logError, logInfo, logWarn } from "@/helpers/logError";
import { normalizePageSizeToSupportedOption, WEB_FORM_PAGE_SIZES } from "@/helpers/filters";
import { getAdminSmallFileUploadLane, type AdminSmallFileApi } from "@/services/uploads/adminSmallFileUploadLane";
import {
	isUploadProgressConflict,
	newUploadId,
	UploadsFacadeClient,
	type UploadsCreateRequest,
	type UploadsFacadeTransport
} from "@uploads";
import { redactedUploadFailure, uploadFailureLogDetails } from "@/services/uploads/uploadLogRedaction";
import { scrubClaimToken } from "@/helpers/scrubClaimToken";
import { phoneSearchPatterns, textSearchPatterns } from "@/helpers/directoryQuery";
import timezones from "@/helpers/timezones";
import Checklist from "@/types/Checklist";
import ChecklistItem from "@/types/ChecklistItem";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import type { ProjectChecklistItemsResponse } from "@/types/ProjectChecklistsResponse";
import { SSEEventType, SSE_PREFIXES, SSE_PREFIX_LENGTHS } from "@/types/api/SSEEventType";
import type { MarketingBannerResponse } from "../types/MarketingBanner";
import type { WhatsNewResponse } from "../types/WhatsNew";
import type { CoachingVideosResponse } from "../types/CoachingVideos";
import type { RecordingCaptionsResponse } from "../types/RecordingCaptions";
import type { OrganizationUploadLink, OrganizationUploadLinkStatus } from "../types/organization/OrganizationUploadLink";
import type { CustomerReviewCardResponse, CustomerReviewListQuery, CustomerReviewListResponse } from "../types/CustomerReview";

const PROJECT_SHARING_PAGE_SIZE = 1000;

/**
 * The page size `searchCustomers` uses when a caller does not choose one. Named rather than left as
 * a bare `10` inside the request body, because a caller that DOES need a different bound has to be
 * able to see what it is overriding.
 *
 * It stays 10, not 20: `searchCustomers` is shared by CustomerAutoComplete (x2),
 * CreateCustomerDialog and CustomerPage, and doubling the rows those four fetch is a separate
 * decision with its own performance story. Only the caller that needs a matched bound asks for one.
 */
export const DEFAULT_CUSTOMER_SEARCH_PAGE_SIZE = 10;

type ProjectChecklistItemUpdate = Pick<ProjectChecklistItemsResponse, "checklistItemId" | "completed" | "notes">;
type UpdateProjectChecklistItemRequest = Pick<ProjectChecklistItemsResponse, "completed" | "notes">;

function parseWarningHeader(warningHeader: string | null): { code: string; description: string }[] {
	if (!warningHeader) {
		return [];
	}

	return warningHeader
		.split(/[;,]/)
		.map((part) => part.trim())
		.filter(Boolean)
		.map((part) => {
			const words = part.split(/\s+/);
			const code = words.shift() || "";
			return { code, description: words.join(" ") };
		})
		.filter((warning) => warning.code || warning.description);
}

const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);
const MAX_RETRIES = 2;
// 425 (TooEarly) is only sent after the server's own POLL_TIMEOUT_MS (25s) poll
// loop already elapsed without the concurrent request completing — a second full
// 25s poll is unlikely to resolve any differently than the first. Capping 425 to a
// single retry (vs. 503's full MAX_RETRIES) bounds the worst case at roughly
// 25s + 5s (Retry-After) + 25s ≈ 55s instead of compounding to ~85s across three
// 25s polls, none of which today can be cancelled (post/put/delete and the entity
// factory don't accept an AbortSignal to plumb through to `_fetchWithRetry`).
const MAX_RETRIES_425 = 1;
// Hard cumulative ceiling on the SLEEP time this function spends between attempts
// (not counting the request/poll round-trip itself, which is server-side and out
// of the client's control without a plumbed AbortSignal — see the comment above).
// Deliberately set below MAX_RETRIES(2) * MAX_RETRY_DELAY_MS(5s) = 10s, the
// mathematical ceiling two maxed-out Retry-After waits would otherwise reach: this
// stops that exact "two consecutive 503s with a 5s Retry-After" case at ~1 wait
// instead of 2, closer to this ticket's original "hard cap ... 5s total" backoff
// budget, while still allowing a full 5s wait plus headroom for one smaller
// exponential-backoff wait on top of it.
const MAX_CUMULATIVE_RETRY_WAIT_MS = 8_000;
const BASE_RETRY_DELAY_MS = 1_000;
const MAX_RETRY_DELAY_MS = 5_000;
// Jitter added to exponential backoff to avoid retry stampedes when many clients see
// the same 503 during a load-balancer event. 250 ms is small relative to the 1 s
// base delay and 5 s cap, so retries stay perceptually snappy.
const RETRY_JITTER_MS = 250;
const RETRYABLE_STATUSES = new Set([503, 425]);
// Server-side fastifyIdempotency middleware sets one of these headers on every
// 2xx fresh write (`Stored`) or replay (`Replayed`) — and on its own 503/425
// signaling errors (`Stored`) so retry-aware clients honor the Retry-After.
// Without one of them present, the client cannot tell whether the response
// came from a route with replay defence or from one that has none — retrying
// the latter could double-mutate. See LSP-15857.
const IDEMPOTENCY_STORED_HEADER = "Idempotency-Stored";
const IDEMPOTENCY_REPLAYED_HEADER = "Idempotency-Replayed";
// Entities whose server-side route config already derives an idempotency key from
// the request BODY (phone/name/externalId — see apps/contact-api/src/routes/
// {customers,labels,projects}.ts `idempotencyBodyKey`), rather than trusting a
// client-supplied header. That body-derived key is content-stable, so it catches a
// double-submit across two *separate* requests (e.g. a genuine double-click, each
// becoming its own `_fetchWithRetry` call). If `_fetchWithRetry` attached its own
// fresh random `Idempotency-Key` here, the server's header-present branch would
// short-circuit before the body derivation ever runs (see `fastifyIdempotency.ts`'s
// key-precedence comment), silently disabling that existing dedup — a random,
// call-scoped header is not a substitute for a stable, content-derived one. The
// entity factory below skips `_fetchWithRetry` for exactly these entities and keeps
// them on plain `_fetch`, matching pre-LSP-15857 behavior; only `createConversation`
// and other entities with no body-derived key get the new retry.
const ENTITIES_WITH_SERVER_BODY_DERIVED_IDEMPOTENCY_KEY = new Set(["Customer", "Label", "Project"]);

function isMutating(options: RequestInit | undefined): boolean {
	const method = (options?.method ?? "GET").toUpperCase();
	return MUTATING_METHODS.has(method);
}

function isProtectedByIdempotency(response: Response): boolean {
	// Case-insensitive on the value so a future server-side casing change ("True",
	// "TRUE") doesn't silently disable retries. Header name lookups via
	// Headers.get(...) are already case-insensitive.
	const stored = response.headers.get(IDEMPOTENCY_STORED_HEADER);
	const replayed = response.headers.get(IDEMPOTENCY_REPLAYED_HEADER);
	return stored?.toLowerCase() === "true" || replayed?.toLowerCase() === "true";
}

function abortError(): Error {
	return Object.assign(new Error("Aborted"), { name: "AbortError" });
}

function abortableSleep(ms: number, signal: AbortSignal | null | undefined): Promise<void> {
	return new Promise((resolve, reject) => {
		if (signal?.aborted) {
			reject(abortError());
			return;
		}
		const timer = setTimeout(() => {
			signal?.removeEventListener("abort", onAbort);
			resolve();
		}, ms);
		const onAbort = () => {
			clearTimeout(timer);
			reject(abortError());
		};
		signal?.addEventListener("abort", onAbort, { once: true });
	});
}

const RETRY_EXHAUSTED_USER_MESSAGE =
	"We're having trouble saving this right now. We tried a few times already — please wait a moment and try again.";

// Retryable status, but the server gave no idempotency-protection signal, so
// `_fetchWithRetry` never attempted a retry at all (see the `!isProtected` branch
// below) -- distinct copy from RETRY_EXHAUSTED_USER_MESSAGE, which specifically
// says "we tried a few times already". Saying that here would be false: zero
// retries happened.
const RETRY_UNAVAILABLE_USER_MESSAGE = "We're having trouble saving this right now. Please wait a moment and try again.";

// `_handleResponse` would otherwise surface the server's raw JSON `message`
// verbatim as the user-facing toast (e.g. "Please retry with the same idempotency
// key" from `sendIdempotencyUnavailable`/`sendIdempotencyTooEarly`) -- backend
// jargon the user has no way to act on. Per this repo's "User-Facing Copy"
// convention (friendly, reassuring, no backend jargon), remap the message before
// returning, preserving status/headers/other fields so downstream error handling
// (e.g. `_handleResponse`'s `errorCode` switch) still works. A response that
// isn't valid JSON is left untouched.
async function withFriendlyMessage(response: Response, friendlyMessage: string): Promise<Response> {
	const contentType = response.headers.get("Content-Type");
	if (!contentType || contentType.indexOf("application/json") === -1) {
		return response;
	}
	try {
		const body = await response.clone().json();
		// Arrays pass `typeof body === "object"` but a non-index `.message`
		// property set on one is dropped by JSON.stringify -- exclude them so the
		// (unlikely) array-shaped error body round-trips unchanged instead of
		// silently losing the remap.
		if (body && typeof body === "object" && !Array.isArray(body)) {
			body.message = friendlyMessage;
		}
		const rewritten = new Response(JSON.stringify(body), { status: response.status, headers: response.headers });
		// A JS-constructed Response always has url === "" (spec behavior) -- restore
		// the original so `_handleResponse`'s error branch can still log/report it.
		// This is exactly the retry-exhaustion path LSP-15857 exists to make
		// diagnosable, so losing the URL here would defeat that purpose.
		Object.defineProperty(rewritten, "url", { value: response.url });
		return rewritten;
	} catch (parseError) {
		// Malformed JSON body -- return the original response unchanged rather than
		// risk masking a different failure behind a confusing secondary error.
		logInfo("Failed to parse response body while remapping a user-facing message", {
			url: response.url,
			status: response.status,
			error: parseError instanceof Error ? parseError.message : String(parseError)
		});
		return response;
	}
}

async function withFriendlyRetryExhaustedMessage(response: Response): Promise<Response> {
	return withFriendlyMessage(response, RETRY_EXHAUSTED_USER_MESSAGE);
}

function parseRetryAfter(headerValue: string | null): number | undefined {
	if (!headerValue || !headerValue.trim()) return undefined;
	const seconds = Number(headerValue);
	// Strictly `> 0`, not `>= 0`: `Number("0")` and, more subtly, `Number(" ")`
	// (a whitespace-only header) both coerce to `0`. Accepting `0` here would
	// return a computed delay of `0`, which `retryAfterMs ?? backoffMs` then keeps
	// (0 is not nullish) — firing an immediate zero-delay retry storm up to
	// MAX_RETRIES times instead of falling back to jittered backoff.
	if (Number.isFinite(seconds) && seconds > 0) {
		return Math.min(seconds * 1_000, MAX_RETRY_DELAY_MS);
	}
	// Retry-After can also be an HTTP-date; if so, ignore (rare for our backend, and
	// honouring it would risk a multi-minute UI freeze).
	return undefined;
}

// LSP-20681: the flat DELETE /sparks/:id and POST /sparks/:id/cancel routes accept an optional,
// mutually exclusive conversationId/projectId query-string hint so a ledger-less spark (the normal
// case — a ledger row only exists for deferred/size-gated runs) can be authorized instead of 404ing.
// Query string, not body: contact-api's DELETE bodies are unreliable across clients (mobile's ktor
// DELETE sends none), so this stays symmetric with mobile's own query-param hint.
function buildSparkScopeQuery(scope?: { conversationId?: string; projectId?: string }): string {
	if (scope?.conversationId) {
		return `?conversationId=${encodeURIComponent(scope.conversationId)}`;
	}
	if (scope?.projectId) {
		return `?projectId=${encodeURIComponent(scope.projectId)}`;
	}
	return "";
}

const api = {
	_token: async function () {
		return await auth.getAccessToken();
	},
	_platformIdentityToken: null as string | null,
	_basePath: "",
	_events: {} as Record<string, any[]>,
	_usedRoles: ["Administrator", "User", "View Only"],
	_me: null,
	_mePromise: null as Promise<any> | null,
	_noAccountDialogHandler: null as ((reason?: string, message?: string) => void) | null,
	_refreshInProgress: false,
	_abortPromise: function (signal?: AbortSignal | null) {
		if (!signal) {
			return null;
		}
		if (signal.aborted) {
			return Promise.reject(Object.assign(new Error("Aborted"), { name: "AbortError" }));
		}
		return new Promise((_resolve, reject) => {
			const onAbort = () => {
				signal.removeEventListener("abort", onAbort);
				reject(Object.assign(new Error("Aborted"), { name: "AbortError" }));
			};
			signal.addEventListener("abort", onAbort, { once: true });
		});
	},
	// LSP-23356: the only place admin sets the Datadog user and org. Runs on every /me fetch, so a
	// refresh after an org switch updates both. /me sends firstname/lastname (lowercase).
	_setDatadogContext: function (me: any) {
		if (!me || !me.user) return;

		const userContext = {
			id: me.user.id,
			email: me.user.email,
			name: [me.user.firstname, me.user.lastname].filter(Boolean).join(" ") || undefined,
			organizationId: me.organization?.id,
			role: me.account?.roles?.[0]?.name,
			industry: me.organization?.industry ?? [],
			country: me.organization?.defaultCountryCode ?? null
		};

		datadogLogs.setUser(userContext);
		datadogRum.setUser(userContext);

		if (me.organization?.id) {
			const account = { id: me.organization.id, name: me.organization.name };
			datadogLogs.setAccount(account);
			datadogRum.setAccount(account);
		} else {
			datadogLogs.clearAccount();
			datadogRum.clearAccount();
		}
	},
	accessToken: async function () {
		return await auth.getAccessToken();
	},
	// setToken: function (value: string) {
	// 	this._token = value;
	// },
	// `pageSize` (ls-heroku-ci FULL review at 578ef71f2d, HIGH): the row cap is a PARAMETER because
	// ShareActionDialog fans this call out in parallel with `getUsers` and the two halves of one
	// fan-out must return the same number of rows for the same query — see DIRECTORY_SEARCH_PAGE_SIZE
	// there. It was hardcoded to 10 while the sibling half asked for 20, so a query matching more
	// than 10 contacts truncated the customer side at a different depth than the team side, and the
	// six rows the dialog renders were drawn from two unequally-deep pools. Defaulting to
	// DEFAULT_CUSTOMER_SEARCH_PAGE_SIZE keeps every other caller byte-identical.
	searchCustomers: async function (
		searchInput: string,
		requestOptions?: RequestInit,
		pageSize: number = DEFAULT_CUSTOMER_SEARCH_PAGE_SIZE
	) {
		const where: any = {};
		if (searchInput) {
			// The original per-word predicate: every word must match at least one column.
			const perWordClauses = textSearchPatterns(searchInput).map((pattern) => ({
				$or: [
					{ $firstname$: { $ilike: pattern } },
					{ $lastname$: { $ilike: pattern } },
					{ "$EmailAddresses.emailAddress$": { $ilike: pattern } },
					{ "$PhoneNumbers.phonenumberraw$": { $ilike: pattern } }
				]
			}));
			// PR #3838 (Bugbot HIGH / AI-review finding 1): a phone-shaped query is ALSO matched by its
			// normalized digits as ONE term, because PhoneNumbers.phonenumberraw holds contiguous digits
			// (sometimes "+"-prefixed) while PhoneNumbers.phoneNumber holds formatInternational() WITH
			// spaces — so a query carrying punctuation or split across words could match neither.
			//
			// ADDITIVE, not replacing (AI-review finding 1 at 692b48af60): this function is shared with
			// CustomerAutoComplete (x2), CreateCustomerDialog and CustomerPage. An earlier revision
			// REPLACED the per-word predicate for phone-shaped queries, which tested name/email only
			// against the whole typed string — so a sibling caller's row that used to match per-word
			// (e.g. an email "7075550100@acme.com" against the query "707 555 0100") silently vanished.
			// OR-ing the two keeps the result set a superset of the pre-diff behaviour for every query a
			// user can realistically type. Precisely (ls-heroku-ci review at d2c63ff3fe): it is not a
			// STRICT superset in one edge case — escaping %/_/\ means a query CONTAINING one of those
			// now matches it literally instead of as a wildcard. That narrowing is deliberate: the
			// wildcard behaviour is exactly the bug escapeLikeWildcards exists to fix.
			const phonePatterns = phoneSearchPatterns(searchInput);
			if (phonePatterns) {
				where["$and"] = [
					{
						$or: [
							...phonePatterns.digitPatterns.map((pattern) => ({ "$PhoneNumbers.phonenumberraw$": { $ilike: pattern } })),
							// Belt-and-braces (AI-review finding 6 at d2c63ff3fe): applying the LITERAL renderings to
							// phonenumberraw as well is largely redundant with the digit patterns above, because
							// that column USUALLY stores contiguous digits — a formatted literal like
							// "+1 604 555 1234" cannot match such a value, and the one literal that can (the
							// as-typed string, when the user typed bare digits) is already a digit candidate.
							//
							// Asked again and kept (ls-heroku-ci FULL review at 916f006b68 called these arms
							// dead). "Contiguous digits" is a CONVENTION, not an enforced constraint, and it was
							// checked against the column rather than against the comment: PhoneNumbers.phonenumberraw
							// has no beforeValidate hook and its Sequelize attribute setter is an explicit no-op
							// (`set(_: any) {}` in contact-api db/models/phoneNumber.ts), so the ORM never writes
							// it — values arrive from migrations and raw SQL. contact-api does not trust it either:
							// every server-side phone predicate reads
							// `regexp_replace(coalesce("phonenumberraw", "phoneNumber", ''), '[^0-9]', '', 'g')`
							// (search/PostgresSearch.ts, controllers/conversationViews.ts), which is only
							// meaningful if the column can hold punctuation. Deleting these arms would therefore
							// narrow the search on exactly the rows the normalization exists for, and they cannot
							// return a row the client filter hides.
							...phonePatterns.literalPatterns.map((pattern) => ({ "$PhoneNumbers.phonenumberraw$": { $ilike: pattern } })),
							...phonePatterns.literalPatterns.map((pattern) => ({ "$PhoneNumbers.phoneNumber$": { $ilike: pattern } })),
							{ $and: perWordClauses }
						]
					}
				];
			} else {
				where["$and"] = perWordClauses;
			}
		}

		return await (api as any).getCustomers(
			{
				where: where,
				pageSize: pageSize,
				order: [["createdAt", "DESC"]]
			},
			undefined,
			requestOptions
		);
	},
	getCustomerDuplicates: async function (id: string) {
		const r = await (this as any)._fetch((this as any).buildUrl(`/customers/${id}/duplicates`), await (this as any).buildHeaders());
		return (this as any)._handleResponse(r, []);
	},
	mergeCustomers: async function (targetId: string, body: { sourceId: string; mergedFields: any }) {
		const r = await (this as any)._fetch(
			(this as any).buildUrl(`/customers/${targetId}/merge`),
			await (this as any).buildHeaders({
				method: "POST",
				body: JSON.stringify(body)
			})
		);
		return (this as any)._handleResponse(r);
	},
	getCustomersForCustomerPage: async function (args: any, warnings: any, customHeaders: any) {
		let url = `/customers/customerPage`;
		if (args && args.urlModifier) {
			url = args.urlModifier(url);
		}
		if (arguments.length > 0) {
			const wherejson = encodeURIComponent(JSON.stringify(args.where || {}));
			const orderjson = encodeURIComponent(JSON.stringify(args.order || []));
			const page = args.page || 1;
			const pageSize = args.pageSize || 10;
			url += `?page=${page}&pageSize=${pageSize}&where=${wherejson}&order=${orderjson}`;

			if (args.searchRequest) {
				const nestedSearchRequest = { ...args.searchRequest };
				delete nestedSearchRequest.page;
				delete nestedSearchRequest.pageSize;
				const searchQuery = searchRequestToQueryString(nestedSearchRequest);
				url += `&${searchQuery}`;
			}
		}
		const r = await this._fetch(this.buildUrl(url), await this.buildHeaders(customHeaders));
		return this._handleResponse(r, warnings || []);
	},

	setPlatformIdentityToken: function (value: string) {
		this._platformIdentityToken = value;
	},
	setOrganizationId: async function (id: string, options?: { isExplicitUserIntent?: boolean }) {
		// LSP-17342: `isExplicitUserIntent` distinguishes an org switch the USER asked for from one
		// the app performs on its own (the router guard's switch on authenticated boot). Only the
		// automatic kind may be suppressed by a bfcache-restore recovery that already owns the
		// page's navigation — suppressing an explicit switch is a silent no-op the user cannot see.
		// It defaults to automatic, so an unclassified caller fails toward over-suppression (one
		// dropped attempt, self-healing) rather than toward racing that navigation.
		return await auth.setOrganizationId(id, options);
	},
	getOrganizationId: async function () {
		return await auth.getOrganizationId();
	},
	setNoAccountDialogHandler: function (handler: ((reason?: string, message?: string) => void) | null) {
		this._noAccountDialogHandler = handler;
	},
	openNoAccountDialog: function (reason?: string, message?: string) {
		if (this._noAccountDialogHandler) {
			this._noAccountDialogHandler(reason, message);
			return;
		}
		// LSP-17342 (review 5023486490 #6): App.vue registers this handler in its own init, so a
		// caller that runs earlier — the router's authenticated-boot guard now among them — gets a
		// silent no-op. That gate is the last line of defence against completing boot in the wrong
		// organization, so the dialog going nowhere must not itself be silent.
		logError(
			"api.openNoAccountDialog: no dialog handler registered; the dialog was NOT shown",
			new Error("NoAccountDialogHandlerMissing"),
			{
				reason: reason ?? null,
				hasMessage: Boolean(message)
			}
		);
	},
	on: function (eventName: string, fn: (args: any) => void) {
		this._events[eventName] = this._events[eventName] || [];
		this._events[eventName].push(fn);
	},
	off: function (eventName: string, fn: (args: any) => void) {
		const evts = this._events[eventName];
		if (!evts) return;
		this._events[eventName] = evts.filter((handler: (args: any) => void) => handler !== fn);
	},
	fire: function (eventName: string, args: any) {
		const evts = this._events[eventName];
		if (!evts) {
			return;
		}
		for (let i = 0; i < evts.length; i++) {
			evts[i](args);
		}
	},
	// fetch() throws indistinguishably for true client offline AND server-side
	// connection drops (e.g. Heroku H12 router timeout when the API dyno stalls
	// on Postgres). To pick the right user-facing copy we probe /public/version
	// — the same liveness endpoint contact-live's "rapid" uses (see
	// apps/contact-live/src/plugins/meetings.ts pingUrl). It is unauthenticated
	// and does not touch Postgres/Redis, so it stays up during DB outages.
	_PING_PATH: "/public/version",
	_PING_TIMEOUT_MS: 3000,
	_PING_CACHE_MS: 5000,
	_pingCache: null as { reachable: boolean; expiresAt: number } | null,
	_pingInFlight: null as Promise<boolean> | null,
	_serverIsReachable: async function (): Promise<boolean> {
		const now = Date.now();
		if (this._pingCache && now < this._pingCache.expiresAt) {
			return this._pingCache.reachable;
		}
		if (this._pingInFlight) {
			return this._pingInFlight;
		}
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), this._PING_TIMEOUT_MS);
		const domain = ((window as any).env.VITE_API_ROOT || "").replace(/\/$/, "");
		const probe = (async () => {
			try {
				const response = await fetch(`${domain}${this._PING_PATH}`, { method: "GET", signal: controller.signal });
				return response.ok;
			} catch {
				return false;
			} finally {
				clearTimeout(timeoutId);
			}
		})();
		this._pingInFlight = probe;
		try {
			const reachable = await probe;
			this._pingCache = { reachable, expiresAt: Date.now() + this._PING_CACHE_MS };
			return reachable;
		} finally {
			this._pingInFlight = null;
		}
	},
	_fetch: async function (url: string, options: RequestInit) {
		// Per-request ID lets support correlate the user-visible "Reference" with
		// server access logs and Datadog. Sent as x-request-id so contact-api logs
		// it whenever the request actually reaches the dyno.
		const requestId = uuid.uuidv4();
		// `options.headers` may already be a `Headers` instance (e.g. from
		// `_fetchWithRetry`, which builds one to carry `Idempotency-Key`).
		// Bracket-assigning a property onto a `Headers` instance is inert — it
		// never reaches the wire — so normalize through the `Headers` API
		// instead of assuming a plain object.
		const headers = new Headers(options.headers ?? {});
		headers.set("x-request-id", requestId);
		options.headers = headers;

		try {
			return await fetch(url, options);
		} catch (e: any) {
			// AbortController cancellations should be silent for pages that navigate away mid-flight.
			// Let callers decide whether to ignore or surface this.
			if (e?.name === "AbortError" || e?.code === "ABORT_ERR") {
				throw e;
			}
			e.requestId = requestId;
			const message = typeof e?.message === "string" ? e.message.toLowerCase() : "";
			// Safari uses "Load failed" for the same rejected fetch reported by Chrome/Firefox.
			if (
				message === "load failed" ||
				message.indexOf("failed to fetch") > -1 ||
				message.indexOf("networkerror when attempting to fetch") > -1
			) {
				// Recursion guard: a failing probe never re-probes itself. (It is also called
				// through bare fetch, not _fetch, so this is defense-in-depth.)
				const isProbe = typeof url === "string" && url.indexOf(this._PING_PATH) > -1;
				const reachable = !isProbe && (await this._serverIsReachable());
				if (reachable) {
					e.message = `Sorry, the request did not go through. Please try again in a moment. (Ref: ${requestId.slice(0, 8)})`;
				} else {
					e.message = "You appear to be offline. Please check your connection and try again.";
				}
			}
			throw e;
		}
	},
	_fetchWithRetry: async function (url: string, options: RequestInit): Promise<Response> {
		// Only mutating verbs get the retry+idempotency treatment. GETs go through
		// the plain `_fetch` (their caller decides whether retry is safe).
		if (!isMutating(options)) {
			return this._fetch(url, options);
		}

		const signal = options.signal ?? null;
		if (signal?.aborted) {
			throw abortError();
		}

		// Generate a stable Idempotency-Key for this call. Reuse it across retries
		// so the server-side middleware (Redis lock + cached replay) recognises the
		// follow-up attempt and either replays the cached response or polls the
		// in-progress one. Never overwrite a key the caller already supplied.
		const headers = new Headers(options.headers ?? {});
		if (!headers.has("Idempotency-Key")) {
			// uuid.uuidv4() (already imported, built on crypto.getRandomValues), not
			// crypto.randomUUID() -- the latter is gated behind isSecureContext and
			// is undefined on any http:// non-localhost origin (a LAN-IP dev/QA box
			// being the realistic case), throwing a TypeError before the request is
			// even sent and turning every mutating call into a hard failure.
			headers.set("Idempotency-Key", uuid.uuidv4());
		}
		const optionsWithKey: RequestInit = { ...options, headers };

		const method = (options.method ?? "GET").toUpperCase();
		// `attempt` is a pure iteration counter (backoff exponent, logging, loop
		// bound) -- it does NOT gate any give-up decision. Give-up is decided by
		// two FULLY independent per-status-class counters below, so a 503-then-425
		// sequence and a 425-then-503 sequence both grant each status class its own
		// complete, undiminished budget regardless of order or how many of the
		// OTHER status already occurred. A single shared counter checked against a
		// status-dependent cap looked equivalent for one specific ordering but
		// silently let one status class's attempts eat into the other's budget in
		// the reverse ordering — see git history on this line for the exact
		// mixed-sequence bugs that shape came with.
		let attempt = 0;
		let attempt425 = 0;
		let attemptOther = 0;
		let cumulativeWaitMs = 0;
		// Manual retry loop (not `retryWithBackoff`) because we need to inspect
		// the Response status to decide whether to retry — `retryWithBackoff`
		// only retries on thrown errors, and `fetch` resolves on HTTP errors.
		//
		// Bound is MAX_RETRIES + MAX_RETRIES_425, not just MAX_RETRIES: since
		// `attempt425`/`attemptOther` are now fully independent, EITHER status
		// class can grant a continue up to its own cap regardless of how many of
		// the OTHER status already occurred. So the total number of continues
		// across any interleaving is bounded by the SUM of both caps, never more --
		// a loop bound of just `attempt <= MAX_RETRIES` could exit before a
		// still-valid 425-driven (or, in the pathological worst case, a
		// still-valid 503-driven) continue's give-up branch ever runs, falling
		// through to the "unreachable" throw below with no friendly message. This
		// bound is exact: the give-up branch for whichever status arrives always
		// fires as a `return` before the loop could exit on its own.
		// Tracked so the (now provably unreachable, but kept as a defensive
		// backstop) fall-through branch below can still return a friendly response
		// instead of throwing raw internal error text at the user, if some future
		// change to the loop bound or counters ever reintroduces a fall-through.
		let lastResponse: Response | undefined;
		while (attempt <= MAX_RETRIES + MAX_RETRIES_425) {
			const response = await this._fetch(url, optionsWithKey);
			lastResponse = response;

			const isRetryableStatus = RETRYABLE_STATUSES.has(response.status);
			const isProtected = isProtectedByIdempotency(response);

			if (!isRetryableStatus) {
				return response;
			}

			// Retryable status but server gave no idempotency-protection signal:
			// the route has no replay defence so retrying could double-mutate.
			// Log it — per LSP-15791 silent-failure discipline this fail-condition
			// must leave a Datadog breadcrumb so the next 503 spike is visible.
			if (!isProtected) {
				// logWarn (not logInfo): this is the real unmitigated-failure branch --
				// raw server error surfaces to the user with no retry attempted at all.
				logWarn("Retryable status without idempotency protection — not retrying", {
					url,
					method,
					status: response.status,
					attempt
				});
				// ls-heroku-ci commit 4951d7b, High #3: without this rewrite the
				// server's raw jargon message (e.g. "retry with the same idempotency
				// key" -- an opaque UUID the user never sees) would otherwise reach the
				// UI verbatim, since _handleResponse surfaces response.message as-is.
				return await withFriendlyMessage(response, RETRY_UNAVAILABLE_USER_MESSAGE);
			}

			const is425 = response.status === 425;
			const maxRetriesForStatus = is425 ? MAX_RETRIES_425 : MAX_RETRIES;
			const attemptsForStatus = is425 ? attempt425 : attemptOther;
			if (attemptsForStatus >= maxRetriesForStatus) {
				// `_fetch` mutates `optionsWithKey.headers` in place (see its own
				// comment) to carry the x-request-id it generated for the last
				// attempt -- surface it here so a real 503/425 storm can be
				// correlated to server logs, not just to this client-side log.
				const lastRequestId = new Headers(optionsWithKey.headers).get("x-request-id");
				logError(
					"Idempotent retry gave up after MAX_RETRIES",
					new Error(`HTTP ${response.status} after ${attemptsForStatus + 1} attempts at ${method} ${url}`),
					{
						url: response.url || url,
						method,
						status: response.status,
						attempt,
						attemptsForStatus: attemptsForStatus + 1,
						requestId: lastRequestId
					}
				);
				return await withFriendlyRetryExhaustedMessage(response);
			}

			const retryAfterMs = parseRetryAfter(response.headers.get("Retry-After"));
			const backoffMs =
				retryAfterMs ?? Math.min(BASE_RETRY_DELAY_MS * Math.pow(2, attempt) + Math.random() * RETRY_JITTER_MS, MAX_RETRY_DELAY_MS);

			// Cumulative sleep budget, independent of the per-attempt caps above: two
			// 5s-clamped Retry-After waits back to back would otherwise reach 10s+ of
			// pure sleep before even counting the request/poll round-trip itself. This
			// is the ticket's original "hard cap ... 5s total" acceptance criterion,
			// generalized to a mixed-status sequence.
			if (cumulativeWaitMs + backoffMs > MAX_CUMULATIVE_RETRY_WAIT_MS) {
				const lastRequestId = new Headers(optionsWithKey.headers).get("x-request-id");
				logError(
					"Idempotent retry gave up: cumulative wait budget exceeded",
					new Error(`Cumulative retry wait would exceed ${MAX_CUMULATIVE_RETRY_WAIT_MS}ms at ${method} ${url}`),
					{
						url: response.url || url,
						method,
						status: response.status,
						attempt,
						cumulativeWaitMs,
						nextBackoffMs: backoffMs,
						requestId: lastRequestId
					}
				);
				return await withFriendlyRetryExhaustedMessage(response);
			}

			logInfo("Idempotent retry scheduled", {
				url,
				method,
				attempt,
				status: response.status,
				retryAfterMs: retryAfterMs ?? null,
				backoffMs,
				source: retryAfterMs !== undefined ? "retry-after" : "backoff"
			});

			// Best-effort drain so the response body isn't left dangling — some
			// fetch implementations log noisy warnings otherwise. Log on failure
			// so a future refactor doesn't make this a silent fail-condition.
			try {
				await response.body?.cancel();
			} catch (cancelError) {
				logInfo("Failed to drain response body during retry", {
					url,
					method,
					attempt,
					error: cancelError instanceof Error ? cancelError.message : String(cancelError)
				});
			}

			await abortableSleep(backoffMs, signal);
			cumulativeWaitMs += backoffMs;
			attempt += 1;
			if (is425) {
				attempt425 += 1;
			} else {
				attemptOther += 1;
			}
		}

		// Should be unreachable given the loop-bound reasoning above, but this is a
		// defensive backstop, not a proof: log loudly for an operator, and return a
		// friendly response (or synthesize one) instead of throwing raw internal
		// error text at the user if it's ever hit.
		logError(
			"Idempotent retry loop fell through without returning — _fetchWithRetry logic bug",
			new Error(`_fetchWithRetry fell through for ${method} ${url} after ${attempt} attempt(s)`),
			{ url, method, attempts: attempt }
		);
		if (lastResponse) {
			return await withFriendlyRetryExhaustedMessage(lastResponse);
		}
		return new Response(JSON.stringify({ error: "ServiceUnavailable", code: 503, message: RETRY_EXHAUSTED_USER_MESSAGE }), {
			status: 503,
			headers: { "Content-Type": "application/json" }
		});
	},
	post: async function (path: string, data: any, ignoreAuth: boolean = false) {
		const r = await this._fetchWithRetry(
			this.buildUrl(path),
			await this.buildHeaders(
				{
					method: "POST",
					body: JSON.stringify(data)
				},
				ignoreAuth
			)
		);
		return this._handleResponse(r);
	},
	streamEvents: async function <T = any>(
		path: string,
		options: {
			method?: "GET" | "POST" | "PUT" | "DELETE";
			data?: any;
			onProgress?: (data: any) => void;
			onEvent?: (eventType: string, data: any) => void;
		} = {}
	): Promise<T> {
		const { method = "POST", data, onProgress, onEvent } = options;
		const apiUrl = this.buildUrl(path, { sse: "true" });

		const response = await this._fetch(
			apiUrl,
			await this.buildHeaders({
				method,
				headers: {
					Accept: "text/event-stream"
				},
				body: data ? JSON.stringify(data) : undefined
			})
		);

		if (!response.ok) {
			const error = await response.json().catch(() => ({ error: "Request failed" }));
			throw new Error(error.error || error.message || "Request failed");
		}

		if (!response.body) {
			throw new Error("Response body is null");
		}

		const reader = response.body.getReader();
		const decoder = new TextDecoder();
		let buffer = "";

		return new Promise((resolve, reject) => {
			const processStream = async () => {
				try {
					while (true) {
						const { done, value } = await reader.read();

						if (done) {
							break;
						}

						const chunk = decoder.decode(value, { stream: true });
						buffer += chunk;

						// Process complete events (separated by double newline)
						const events = buffer.split("\n\n");
						buffer = events.pop() || ""; // Keep incomplete event in buffer

						for (const eventBlock of events) {
							if (!eventBlock.trim()) {
								continue;
							}

							let eventType: string = SSEEventType.Message;
							let eventData = "";

							const lines = eventBlock.split("\n");
							for (const line of lines) {
								if (line.startsWith(SSE_PREFIXES.Event)) {
									eventType = line.substring(SSE_PREFIX_LENGTHS.Event).trim();
								} else if (line.startsWith(SSE_PREFIXES.Data)) {
									// Per SSE spec, multiple data: lines are concatenated with newlines
									if (eventData) {
										eventData += "\n";
									}
									eventData += line.substring(SSE_PREFIX_LENGTHS.Data).trim();
								} else if (line.startsWith(SSE_PREFIXES.Comment)) {
									// Comment line, ignore
									continue;
								}
							}

							if (eventData) {
								try {
									const parsed = JSON.parse(eventData);

									if (eventType === SSEEventType.Progress && onProgress) {
										onProgress(parsed);
									} else if (eventType === SSEEventType.Complete) {
										resolve(parsed);
										return;
									} else if (eventType === SSEEventType.Error) {
										const streamError = new Error(parsed.error || "Unknown error");
										if (parsed.code) {
											// Stable server error code (e.g. WEBSITE_ACCESS_BLOCKED) so
											// callers can show specific, user-friendly copy.
											(streamError as Error & { code?: string }).code = parsed.code;
										}
										reject(streamError);
										return;
									} else if (onEvent) {
										onEvent(eventType, parsed);
									}
								} catch (e) {
									console.error("[API] Failed to parse SSE data:", e, "data:", eventData);
								}
							}
						}
					}

					// If we get here without resolving, something went wrong
					reject(new Error("Stream ended without completion."));
				} catch (error: any) {
					reject(error);
				}
			};

			processStream();
		});
	},
	detectStyleWithProgress: async function (url: string, onProgress: (step: string, message: string) => void): Promise<any> {
		return this.streamEvents("/ai/detect-style", {
			method: "POST",
			data: { url },
			onProgress: (progress) => onProgress(progress.step, progress.message)
		});
	},
	put: async function (path: string, data: any, headers?: Record<string, any>) {
		const r = await this._fetchWithRetry(
			this.buildUrl(path),
			await this.buildHeaders({
				method: "PUT",
				body: JSON.stringify(data),
				headers: headers
			})
		);
		return this._handleResponse(r);
	},
	// LSP-16640: thin status flag-write used when a video upload starts. Hits the dedicated
	// txn-free PUT /conversations/:id/status (NOT the heavyweight PUT /:id, whose email/audit/
	// notification graph held the conversation row lock for the whole request and caused H12
	// timeouts when many uploaders flipped the same conversation at once). Tolerates a transient
	// 5xx / network blip with a short linear backoff so one hiccup doesn't fail the whole upload
	// toast; a 4xx fails fast. Transient retries deliberately bypass _handleResponse so its
	// global error toast does not fire on every attempt — only the final/decisive response does.
	updateConversationStatus: async function (id: string, status: string) {
		const maxAttempts = 3;
		for (let attempt = 1; ; attempt++) {
			try {
				const response = await this._fetch(
					this.buildUrl(`/conversations/${id}/status`),
					await this.buildHeaders({
						method: "PUT",
						body: JSON.stringify({ status })
					})
				);
				// 5xx is the transient H12/overload symptom — retry until the last attempt. A 2xx or
				// 4xx is decisive: hand it to _handleResponse (which fires the global error toast on
				// non-ok), so intermediate transient retries never spam that toast.
				if (response.status < 500 || attempt >= maxAttempts) {
					return this._handleResponse(response);
				}
			} catch (e) {
				// Network / CORS / H12 router timeout surfaces as a thrown fetch error — transient.
				if (attempt >= maxAttempts) {
					throw e;
				}
			}
			await new Promise((resolve) => setTimeout(resolve, attempt * 300));
		}
	},
	get: async function (path: string, ignoreAuth: boolean = false, skipCacheBust: boolean = false) {
		const r = await this._fetch(
			this.buildUrl(path, undefined, skipCacheBust),
			await this.buildHeaders(
				{
					method: "GET"
				},
				ignoreAuth
			)
		);
		return this._handleResponse(r);
	},
	delete: async function (path: string, data: any) {
		const r = await this._fetchWithRetry(
			this.buildUrl(path),
			await this.buildHeaders({
				method: "DELETE",
				body: JSON.stringify(data)
			})
		);
		return this._handleResponse(r);
	},
	getToken: async function () {
		const token = await (auth as any).getAccessToken();
		if (!token) {
			throw new UnauthenticatedError("Unable to build authenticated request headers without an access token.");
		}
		return token;
	},
	getProjectVerdicts: async function (projectIds: string[], requestOptions?: ApiRequestOptions): Promise<ProjectAccessVerdictsResponse> {
		if (!requestOptions) return this.post("/projects/access-verdicts", { projectIds });
		const response = await this._fetch(this.buildUrl("/projects/access-verdicts"), await this.buildHeaders({
			...requestOptions, method: "POST", body: JSON.stringify({ projectIds })
		}));
		return this._handleResponse(response);
	},
	getProjectVerdict: async function (projectId: string, requestOptions?: ApiRequestOptions): Promise<ProjectAccessVerdict | undefined> {
		const path = `/projects/${encodeURIComponent(projectId)}/access-verdict`;
		const response: ProjectAccessVerdictsResponse = requestOptions
			? await this._handleResponse(await this._fetch(this.buildUrl(path), await this.buildHeaders({ ...requestOptions, method: "GET" })))
			: await this.get(path);
		return response?.verdicts?.[projectId] ?? Object.entries(response?.verdicts ?? {}).find(([id]) => id.toLowerCase() === projectId.toLowerCase())?.[1];
	},
	getProjectInsecure: async function (projectId: string, shareId: string) {
		// LSP-21122: encode the share id. A path-form link with a malformed percent-encoded segment reaches
		// here with that raw text (Vue Router keeps it when decoding throws); unencoded, the API's router
		// rejects the undecodable path with 400 FST_ERR_BAD_URL instead of the 404 the recipient's
		// "couldn't find that project" copy is keyed on. A UUID is unchanged.
		const r = await this._fetch(this.buildUrl(`/projects/${projectId}/public/${encodeURIComponent(shareId)}`), {
			method: "GET"
		});
		return this._handleResponse(r);
	},
	// LSP-17770: per-recipient claim link (/share/:token). Context + email-lookup are
	// unauthenticated (the recipient may be signed out); claim is authenticated and
	// binds the invite to the signed-in account.
	getShareClaimContext: async function (token: string) {
		const r = await this._fetch(this.buildUrl(`/share-claims/${encodeURIComponent(token)}`), {
			method: "GET"
		});
		return this._handleResponse(r);
	},
	lookupShareClaimEmail: async function (token: string, email: string) {
		return this.post(`/share-claims/${encodeURIComponent(token)}/email-lookup`, { email }, true);
	},
	claimShare: async function (token: string) {
		return this.post(`/share-claims/${encodeURIComponent(token)}/claim`, {});
	},
	// LSP-17773: re-send an unclaimed invite (re-mints its claim token + resets delivery status).
	resendProjectShareInvite: async function (id: string) {
		return this.post(`/project-share/${encodeURIComponent(id)}/resend`, {});
	},
	// Ask-to-edit supports members and guests; retain the session when signed in.
	requestProjectAccess: async function (projectId: string, shareId: string, email: string, message?: string) {
		let token: string | null = null;
		try {
			if (await auth.isAuthenticated()) {
				token = await auth.getAccessToken({ redirectOnAuthFailure: false });
			}
		} catch {
			// Share-link possession still permits a guest request.
		}
		// Reuse the token; a second lookup could fail or trigger auth recovery.
		const headers = await this.buildHeaders(
			{
				method: "POST",
				body: JSON.stringify({ projectId, shareId, email, message }),
				headers: token ? { authorization: `Bearer ${token}` } : {}
			},
			true
		);
		const response = await this._fetchWithRetry(this.buildUrl("/project-share/request-access"), headers);
		return this._handleResponse(response);
	},
	// LSP-15740: public/read-only checklist fetch for the shared-project view.
	// Bound to a project shareId, so the response is unprivileged data the
	// share recipient is already entitled to read alongside Media and Documents.
	getProjectChecklistsInsecure: async function (projectId: string, shareId: string) {
		const r = await this._fetch(this.buildUrl(`/projects/${projectId}/public/${shareId}/checklists`), {
			method: "GET"
		});
		return this._handleResponse(r);
	},
	// LSP-18733: the radius is a PATH segment, not a query parameter. The route is declared
	// `/nearby/:latitude/:longitude/:radiusInMeters?` and the handler reads `req.params`, so the
	// previous `?radiusInMeters=` form never populated it — it silently fell back to the
	// controller's 500m default. AddressLookup.vue defaults searchRadius to 1000 and
	// NearbyProjects.vue even re-fetches when it changes, so widening the radius fired a request
	// whose results could never change, and projects between 500m and the chosen radius showed as
	// "No Projects Nearby" when they existed.
	getNearbyProjects: async function (latitude: string, longitude: string, radiusInMeters: number = 100) {
		const r = await this._fetch(this.buildUrl(`/projects/nearby/${latitude}/${longitude}/${radiusInMeters}`), {
			method: "GET",
			headers: {
				authorization: `Bearer ${await this.getToken()}`
			}
		});
		return this._handleResponse(r);
	},
	getShareIdForProject: async function (projectId: string) {
		const r = await this._fetch(this.buildUrl(`/projects/${projectId}/public/share`), {
			method: "GET",
			headers: {
				authorization: `Bearer ${await this.getToken()}`
			}
		});
		return this._handleResponse(r);
	},
	// Sheet API methods
	createSheet: async function (body: { projectId: string; name?: string }) {
		return this.post("/sheets", body);
	},
	getSheetsForProject: async function (projectId: string) {
		return this.get(`/sheets/project/${projectId}`);
	},
	updateSheet: async function (sheetId: string, body: Record<string, any>) {
		return this.put(`/sheets/${sheetId}`, body);
	},
	deleteSheet: async function (sheetId: string) {
		return this.delete(`/sheets/${sheetId}`, {});
	},
	updateSheetMedia: async function (sheetId: string, mediaItems: { id: string; type: string; sortOrder: number }[]) {
		return this.put(`/sheets/${sheetId}/media`, { mediaItems });
	},
	getSheetPublic: async function (sheetId: string, accessToken: string) {
		const r = await this._fetch(this.buildUrl(`/sheets/${sheetId}/public/${accessToken}`), {
			method: "GET"
		});
		return this._handleResponse(r);
	},
	shareSheet: async function (
		sheetId: string,
		body: { emails?: string[]; phoneNumbers?: string[] }
	): Promise<{ emailsSent: number; smsSent: number; failedEmails: string[]; failedPhoneNumbers: string[] }> {
		return this.post(`/sheets/${sheetId}/notify`, body);
	},
	shareForm: async function (
		formId: string,
		body: { emails?: string[]; phoneNumbers?: string[]; message?: string }
	): Promise<{ emailsSent: number; smsSent: number; failedEmails: string[]; failedPhoneNumbers: string[] }> {
		return this.post(`/forms/${formId}/notify`, body);
	},
	notifyReportShare: async function (payload: {
		publicUrl: string;
		reportName?: string;
		externalType: string;
		externalId: string;
		emails?: string[];
		phoneNumbers?: string[];
	}): Promise<{ emailsSent: number; smsSent: number; failedEmails: string[]; failedPhoneNumbers: string[] }> {
		return this.post(`/reports/share-notify`, payload);
	},
	getCommentsInsecure: async function (body: any, projectId: string, shareId: string) {
		const r = await this._fetch(
			this.buildUrl("/public/comments", {
				pageSize: body.pageSize || 50,
				where: JSON.stringify(body.where || {}),
				projectId,
				shareId
			}),
			{
				method: "GET"
			}
		);
		return this._handleResponse(r);
	},
	// LSP-19516 — the route now authorizes on the share-link credential the visitor already holds
	// (`?shareId=` in the URL). This call site has no session by construction: it is only reached on
	// the `!isAuthenticated` branch of the shared-project view, so shareId is the only credential
	// available. Without it the API returns 404 rather than the project's whole artifact list.
	getArtifactsForProjectInsecure: async function (id: string, shareId: string, artifactType: string) {
		const r = await this._fetch(this.buildUrl(`/projects/${id}/public/artifacts`, { shareId, artifactType }), {
			method: "GET"
		});
		return this._handleResponse(r);
	},
	getRecordingsForProject: async function (id: string) {
		const r = await this._fetch(this.buildUrl(`/projects/${id}/recordings`), {
			method: "GET",
			headers: {
				authorization: `Bearer ${await this.getToken()}`
			}
		});
		return this._handleResponse(r);
	},
	getSnapshotsForProject: async function (id: string, params?: { page?: number; pageSize?: number }) {
		const r = await this._fetch(this.buildUrl(`/projects/${id}/snapshots`, params), {
			method: "GET",
			headers: {
				authorization: `Bearer ${await this.getToken()}`
			}
		});
		return this._handleResponse(r);
	},
	getCaptureEvidence: async function (sourceType: "snapshot" | "recording", sourceId: string) {
		const r = await this._fetch(this.buildUrl(`/capture-evidence/${sourceType}/${encodeURIComponent(sourceId)}`), {
			method: "GET",
			headers: { authorization: `Bearer ${await this.getToken()}` }
		});
		return this._handleResponse(r);
	},
	getCaptureEvidenceArtifacts: async function (sourceType: "snapshot" | "recording", sourceId: string) {
		const r = await this._fetch(this.buildUrl(`/capture-evidence/${sourceType}/${encodeURIComponent(sourceId)}/artifacts`), {
			method: "GET",
			headers: { authorization: `Bearer ${await this.getToken()}` }
		});
		return this._handleResponse(r);
	},
	cropCaptureObjectAtPoint: async function (
		conversationId: string,
		sourceClientId: string,
		sourceRevision: string,
		xNormalized: number,
		yNormalized: number,
		maskRank = 0
	) {
		const r = await this._fetch(
			this.buildUrl(
				`/capture-evidence/${encodeURIComponent(conversationId)}/snapshot/${encodeURIComponent(sourceClientId)}/object-crop`
			),
			{
				method: "POST",
				body: JSON.stringify({ sourceRevision, xNormalized, yNormalized, ...(maskRank ? { maskRank } : {}) }),
				headers: { "Content-Type": "application/json", authorization: `Bearer ${await this.getToken()}` }
			}
		);
		return this._handleResponse(r);
	},
	submitCaptureEvidenceManifest: async function (input: {
		conversationId: string;
		sourceType: "snapshot" | "recording";
		sourceClientId: string;
		sourceRevision: string;
		clientSchemaVersion: number;
		manifest: Record<string, unknown>;
	}) {
		const r = await this._fetch(this.buildUrl("/public/capture-evidence/manifest"), {
			method: "POST",
			body: JSON.stringify(input),
			headers: { "Content-Type": "application/json", authorization: `Bearer ${await this.getToken()}` }
		});
		return this._handleResponse(r);
	},
	getFilesForProject: async function (id: string, params?: { page?: number; pageSize?: number }) {
		const r = await this._fetch(this.buildUrl(`/projects/${id}/files`, params), {
			method: "GET",
			headers: {
				authorization: `Bearer ${await this.getToken()}`
			}
		});
		return this._handleResponse(r);
	},
	getPlatformIdentityToken: function () {
		return this._platformIdentityToken;
	},
	replaceUser: async function (currentUserId: string, request: any): Promise<any> {
		return this.put(`/users/${currentUserId}/replace`, request);
	},
	signupUser: async function (): Promise<any> {
		const userId = StorageWrapper.getItem("userId") || "";
		const user = userId !== "" ? { id: userId } : {};
		return this.post(`/signup/user`, user);
	},
	getBillableUserCount: async function (): Promise<any> {
		const response = await this.get(`/users/billable-count`);
		return response.count;
	},
	signupOrganization: async function (org: any): Promise<any> {
		return this.post(`/signup/organization`, org);
	},
	sendInvitation: async function (conversationId: string, channel?: "sms" | "email"): Promise<SendInvitationResponse> {
		return this.post(`/conversations/sendInvitation`, channel ? { conversationId, channel } : { conversationId });
	},
	shareConversation: async function (request: any): Promise<any> {
		return this.post(`/conversations/share`, request);
	},
	// LSP-17328: manually fire the pending-upload reminder for a session whose recording never
	// finished uploading. Server-side this reuses the same email/SMS the automatic content-notification
	// job sends, and enforces a Redis cooldown — a 429 carries `retryAfter` in seconds.
	sendUploadReminder: async function (conversationId: string): Promise<any> {
		return this.post(`/conversations/${conversationId}/upload-reminder`, {});
	},
	getSparks: async function (conversationId: string): Promise<any> {
		return this.get(`/conversations/${conversationId}/sparks`);
	},
	getSparksBulk: async function (conversationIds: string[]): Promise<any[]> {
		if (conversationIds.length === 0) return [];
		return this.post(`/conversations/sparks/bulk`, { conversationIds });
	},
	// LSP-18409 (single-door): a PROJECT report run goes through contact-api, which resolves the OWNER
	// org, enforces the owner's quota, mints the owner's Scribe token, dispatches, and bills the owner —
	// replacing the browser's gate + direct-Scribe dispatch for projects so cross-org runs home in (and
	// bill) the owner org, not the caller's.
	createProjectSpark: async function (projectId: string, body: Record<string, unknown>): Promise<any> {
		return this.post(`/projects/${projectId}/sparks`, body);
	},
	// Share-aware aggregate list (owner tenant) — the SAME reports for owner and external editor.
	getProjectSparks: async function (projectId: string): Promise<any> {
		return this.get(`/projects/${projectId}/sparks`);
	},
	getReports: async function (
		page: number,
		pageSize: number,
		sortBy: string,
		sortDir: string,
		statusFilter: string = "all",
		creatorIds?: string[],
		search?: string,
		createdAtFrom?: string,
		createdAtTo?: string
	): Promise<any> {
		const params = new URLSearchParams();
		params.set("page", String(page));
		params.set("pageSize", String(pageSize));
		params.set("sortBy", sortBy);
		params.set("sortDir", sortDir);
		params.set("status", statusFilter);
		// The wire param stays singular (creatorId) — contact-api's route never renamed it, only
		// widened it to accept this same comma-separated form (LSP-20463).
		if (creatorIds && creatorIds.length > 0) params.set("creatorId", creatorIds.join(","));
		if (search) params.set("search", search);
		if (createdAtFrom) params.set("createdAtFrom", createdAtFrom);
		if (createdAtTo) params.set("createdAtTo", createdAtTo);
		return this.get(`/reports?${params.toString()}`);
	},
	getReportCreators: async function (): Promise<any> {
		return this.get(`/reports/creators`);
	},
	// LSP-18409 (owner-tenant packs): for a cross-org shared project the Run-A-Report picker must offer
	// the OWNER's packs (the report dispatches under the owner tenant), so load them through the server.
	getProjectPacks: async function (projectId: string, query?: string): Promise<any> {
		return this.get(`/projects/${projectId}/packs${query ? `?${query}` : ""}`);
	},
	getProjectPack: async function (projectId: string, packId: string): Promise<any> {
		return this.get(`/projects/${projectId}/packs/${packId}`);
	},
	// Single-door report CONTENT reads (owner tenant), replacing the browser's direct-Scribe reads for
	// project reports so a cross-org editor opens exactly what the owner sees.
	getProjectSparkAnalysis: async function (projectId: string, analysisId: string): Promise<any> {
		return this.get(`/projects/${projectId}/sparks/${analysisId}`);
	},
	getProjectSparkRawAnalysis: async function (
		projectId: string,
		analysisId: string,
		options?: { includeReferences?: boolean; resolveReferences?: boolean }
	): Promise<any> {
		const params = new URLSearchParams();
		if (options?.includeReferences) params.set("includeReferences", "true");
		if (options?.resolveReferences) params.set("resolveReferences", "true");
		const query = params.toString();
		return this.get(`/projects/${projectId}/sparks/${analysisId}/raw${query ? `?${query}` : ""}`);
	},
	getProjectSparkTaskResults: async function (projectId: string, analysisId: string): Promise<any> {
		return this.get(`/projects/${projectId}/sparks/${analysisId}/task-results`);
	},
	// LSP-16433: oversized-document omissions keyed by Scribe analysisId — the reliable bridge to merge an
	// omission onto its report row (row.id === analysisId), since Scribe results echo no packId/externalId.
	getExcludedDocumentsByAnalyses: async function (analysisIds: string[]): Promise<any[]> {
		return this.post(`/conversations/analyses/excluded-documents`, {
			analysisIds
		});
	},
	// LSP-16433: stamp the Scribe analysisId onto a gate-created ledger row after the browser dispatches.
	linkAnalysisToSchedule: async function (scheduleId: string, analysisId: string): Promise<any> {
		return this.post(`/conversations/analysis-schedules/${scheduleId}/analysis`, { analysisId });
	},
	// Size-gate and assemble a conversation's Scribe payload before the browser dispatches to Scribe.
	// contact-api does NOT call Scribe here; the browser uses the returned files/metadata with its Scribe token.
	gateConversationAnalysis: async function (conversationId: string, body: Record<string, unknown>): Promise<any> {
		return this.post(`/conversations/${conversationId}/analysis-gate`, body);
	},
	// Same payload assembly path for a project run.
	gateProjectAnalysis: async function (projectId: string, body: Record<string, unknown>): Promise<any> {
		return this.post(`/projects/${projectId}/analysis-gate`, body);
	},
	// Spark delete (any state) routed through contact-api: deletes the scribe analysis AND the schedule row.
	cancelSpark: async function (conversationId: string, sparkId: string): Promise<any> {
		return this.post(`/conversations/${conversationId}/sparks/${sparkId}/cancel`, {});
	},
	// LSP-20681: optional conversationId/projectId hint (mutually exclusive) — see buildSparkScopeQuery.
	cancelSparkById: async function (sparkId: string, scope?: { conversationId?: string; projectId?: string }): Promise<any> {
		return this.post(`/sparks/${sparkId}/cancel${buildSparkScopeQuery(scope)}`, {});
	},
	// LSP-16746: cancel a scheduled call in place (notifies the recipient + tears down the
	// Appointment/reminders) without deleting the conversation itself.
	cancelScheduledCall: async function (conversationId: string): Promise<any> {
		return this.post(`/conversations/${conversationId}/cancel-scheduled-call`, {});
	},
	// LSP-16658: move a scheduled call to a new date/time. The Appointment is updated in place, so
	// this is a POST beside the cancel route rather than a delete-and-recreate.
	//
	// `endAt` is deliberately NOT in the contract — the server preserves the booked duration.
	//
	// `timeZone` is required here so the zone the contact-facing copy is rendered in is explicit and
	// deterministic rather than falling through to the organization's. It is NOT a fix for the stored
	// notification template's date, and an earlier revision of this comment wrongly claimed it was:
	// the template's `${scheduledDate}` was substituted at BOOKING time in the BOOKER's browser zone
	// (ActionSchedule.vue `updateTemplate`), while the only zone this client can supply is the
	// ADMIN's (`moment.tz.guess()`). `Appointments` stores no zone, so the booking zone is not
	// recoverable by either side. Whenever the two differ, the server's patch renders a needle that
	// is not in the stored text, logs TemplateDateNotPatched, and returns 200 with the OLD date still
	// in the copy. Closing that needs the booking zone persisted (or a zone-independent patch)
	// server-side — tracked separately; do not re-justify it from this client.
	rescheduleScheduledCall: async function (
		conversationId: string,
		body: { startAt: number; timeZone: string; note?: string }
	): Promise<any> {
		return this.post(`/conversations/${conversationId}/reschedule-scheduled-call`, body);
	},
	deleteSpark: async function (conversationId: string, sparkId: string): Promise<any> {
		return this.delete(`/conversations/${conversationId}/sparks/${sparkId}`, {});
	},
	// LSP-20681: optional conversationId/projectId hint (mutually exclusive) — see buildSparkScopeQuery.
	deleteSparkById: async function (sparkId: string, scope?: { conversationId?: string; projectId?: string }): Promise<any> {
		return this.delete(`/sparks/${sparkId}${buildSparkScopeQuery(scope)}`, {});
	},
	// Legacy planned-report cancel for rows not yet in scribe (no analysisId) — destroys the schedule row.
	cancelLegacySchedule: async function (conversationId: string, scheduleId: string): Promise<any> {
		return this.delete(`/conversations/${conversationId}/analysis-schedules/${scheduleId}`, {});
	},
	getRoles: async function () {
		const roles = await this.get(`/authorization/roles`);
		return roles.filter((x: { name: string }) => this._usedRoles.includes(x.name));
	},
	getConversationsStatuses: async function (ids: string): Promise<any> {
		const conversations = await this.get(`/conversations/status/${ids}`);
		return conversations;
	},
	getConversationsDelivery: async function (ids: string[]): Promise<ConversationDelivery[]> {
		if (ids.length === 0) return [];
		return this.post("/conversations/delivery/bulk", { conversationIds: ids });
	},
	// LSP-17772: the one conversation read extended to cross-org share-origin editors; used by
	// MediaLightBox to learn the uploader identity (conversationCreatedBy) the server's
	// own-uploads-only gates compare against.
	getConversationUploadStatus: async function (id: string): Promise<any> {
		return this.get(`/conversations/${id}/upload-status`);
	},
	getConversationInsecure: async function (id: string): Promise<any> {
		return this.get(`/public/conversations/${id}`);
	},
	completeSubscription: async function (subscription: any): Promise<any> {
		return this.post(`/subscriptions/complete`, subscription);
	},
	updateSubscriptionBillingAdmin: async function (externalSubscriptionId: string, newBillingAdminId: string): Promise<any> {
		return this.put(`/subscriptions/${externalSubscriptionId}/billing-admin`, { billingAdminAccountId: newBillingAdminId });
	},
	getSeatCount: async function (includeAddons: boolean = true): Promise<any> {
		const response = await this.get(`/subscriptions/total-seats?includeAddons=${includeAddons}`);
		return response.count;
	},
	createRecordingToken: async function ({
		meetingId,
		recordingId,
		conversationId
	}: {
		meetingId: string;
		recordingId: string;
		conversationId: string;
	}): Promise<any> {
		const r = await this._fetch(this.buildUrl(`/recordings/token`), {
			method: "POST",
			body: JSON.stringify({
				meetingId: meetingId,
				recordingId: recordingId,
				conversationId: conversationId
			}),
			headers: {
				"Content-Type": "application/json",
				authorization: `Bearer ${await this.getToken()}`
			}
		});
		return this._handleResponse(r);
	},
	createPlatformIdentity: async function (userId: string): Promise<any> {
		const response = await this._fetch(this.buildUrl(`/tokens/platform`), {
			method: "POST",
			body: JSON.stringify({ userId: userId }),
			headers: {
				"Content-Type": "application/json",
				authorization: `Bearer ${await this.getToken()}`
			}
		});
		return this._handleResponse(response);
	},
	getArtifactFromToken: async function (token: string): Promise<any> {
		return this.get(`/public/artifacts/${token}`, true);
	},
	updateTagsForEntity: async function (entityType: string, entityId: string, tags: any, headers?: Record<string, any>): Promise<any> {
		return this.put(`/${entityType}/tags/${entityId}`, tags, headers);
	},
	/**
	 * Updates the createdAt date of a snapshot or recording.
	 * @param artifactType - "snapshots" or "recordings"
	 * @param artifactId - The ID of the artifact to update
	 * @param createdAt - The new date/time string (ISO 8601)
	 */
	updateArtifactDate: async function (artifactType: string, artifactId: string, createdAt: string): Promise<any> {
		return this.put(`/${artifactType}/${artifactId}/date`, { createdAt });
	},
	getRecordings: async function (conversationId: string): Promise<any> {
		const r = await this._fetch(this.buildUrl(`/recordings/${conversationId}`), {
			method: "GET",
			headers: {
				authorization: `Bearer ${await this.getToken()}`
			}
		});
		return this._handleResponse(r);
	},
	/**
	 * LSP-21125: the caption lines saved during the live call this MasterRecording came from, merged per
	 * utterance, for the lightbox Translation panel. Rejects like every other read here (404 for a recording
	 * with no channel/conversation); the panel is optional, so its caller treats any rejection as "no lines".
	 */
	getRecordingCaptions: async function (masterRecordingId: string): Promise<RecordingCaptionsResponse> {
		return this.get(`/master-recordings/${encodeURIComponent(masterRecordingId)}/captions`);
	},
	getHomeDashboard: async function (): Promise<any> {
		const r = await this._fetch(this.buildUrl(`/home/dashboard`), {
			method: "GET",
			headers: {
				authorization: `Bearer ${await this.getToken()}`
			}
		});
		return this._handleResponse(r);
	},
	getMarketingBanner: async function (): Promise<MarketingBannerResponse | null> {
		return this.get(`/marketing/banner`);
	},
	getWhatsNew: async function (): Promise<WhatsNewResponse> {
		return this.get(`/marketing/whats-new`);
	},
	getCoachingVideos: async function (): Promise<CoachingVideosResponse> {
		return this.get(`/marketing/coaching-videos`);
	},
	getRecentProjects: async function (
		limit: number = 4,
		filters?: { statuses?: string[]; labels?: string[]; latitude?: number; longitude?: number }
	): Promise<any[]> {
		let url = `/projects/recent?limit=${limit}`;
		if (filters?.statuses?.length) {
			url += `&statuses=${filters.statuses.join(",")}`;
		}
		if (filters?.labels?.length) {
			url += `&labels=${filters.labels.join(",")}`;
		}
		if (filters?.latitude != null && filters?.longitude != null) {
			url += `&latitude=${filters.latitude}&longitude=${filters.longitude}`;
		}
		return this.get(url);
	},
	updateProjectThumbnail: async function (projectId: string, snapshotId: string | null): Promise<any> {
		return this.put(`/projects/${projectId}/thumbnail`, { snapshotId });
	},
	// LSP-15828 — lightweight project list for the Live Call editor picker.
	// Server-side filters by user editor-share + name ILIKE; no need for the
	// admin client to re-fetch project-shares. Response shape is `{ rows }`
	// only — the server intentionally omits `count` because the picker
	// never paginates (see getProjectsLite in apps/contact-api).
	getProjectsLite: async function (
		args: { where?: any; pageSize?: number },
		options?: { signal?: AbortSignal }
	): Promise<{ rows: any[] }> {
		const wherejson = encodeURIComponent(JSON.stringify(args?.where || {}));
		const pageSize = args?.pageSize || 25;
		const url = `/projects/lite?pageSize=${pageSize}&where=${wherejson}`;
		const abortPromise = this._abortPromise(options?.signal);
		const fetchPromise = (async () => {
			const r = await this._fetch(this.buildUrl(url), await this.buildHeaders());
			return this._handleResponse(r);
		})();
		return abortPromise ? Promise.race([fetchPromise, abortPromise]) : fetchPromise;
	},
	getDefaultForm: async function (): Promise<any> {
		const response = await this.get(`/forms/default/layout`);
		return response;
	},
	getFormInsecure: async function (formId: string): Promise<any> {
		return this.get(`/public/forms/${formId}`, true);
	},
	// LSP-19563: the public form's Address field proxies every Google Places call through the API so the
	// browser never holds a Maps key. All five are form-gated and unauthenticated (ignoreAuth = true).
	getPublicFormAddressSuggestions: async function (
		formId: string,
		params: { input: string; session: string; latitude?: number; longitude?: number; region?: string }
	): Promise<any> {
		const query: Record<string, string> = { input: params.input, session: params.session };
		if (params.latitude != null && params.longitude != null) {
			query.latitude = String(params.latitude);
			query.longitude = String(params.longitude);
		}
		if (params.region) {
			query.region = params.region;
		}
		const r = await this._fetch(
			this.buildUrl(`/public/forms/${formId}/address/suggestions`, query),
			await this.buildHeaders({ method: "GET" }, true)
		);
		return this._handleResponse(r);
	},
	getPublicFormAddressPlace: async function (formId: string, placeId: string, session: string): Promise<any> {
		const r = await this._fetch(
			this.buildUrl(`/public/forms/${formId}/address/places/${encodeURIComponent(placeId)}`, { session }),
			await this.buildHeaders({ method: "GET" }, true)
		);
		return this._handleResponse(r);
	},
	reverseGeocodePublicFormAddress: async function (formId: string, latitude: number, longitude: number): Promise<any> {
		const r = await this._fetch(
			this.buildUrl(`/public/forms/${formId}/address/reverse-geocode`, { latitude: String(latitude), longitude: String(longitude) }),
			await this.buildHeaders({ method: "GET" }, true)
		);
		return this._handleResponse(r);
	},
	geocodePublicFormAddress: async function (formId: string, address: string): Promise<any> {
		const r = await this._fetch(
			this.buildUrl(`/public/forms/${formId}/address/geocode`, { address }),
			await this.buildHeaders({ method: "GET" }, true)
		);
		return this._handleResponse(r);
	},
	// Static map tile for the picked address. Cache-busting is skipped (skipCacheBust = true) so the
	// <img> src stays stable and the browser can reuse the cached image across re-renders.
	publicFormAddressMapUrl: function (formId: string, latitude: number, longitude: number): string {
		return this.buildUrl(`/public/forms/${formId}/address/map`, { latitude: String(latitude), longitude: String(longitude) }, true);
	},
	submitCustomWebForm: async function (form: any): Promise<any> {
		const response = await this.post("/form-submissions/public", form, true);
		return response;
	},
	deleteArtifacts: async function (artifactType: string, artifacts: any[], conversationId: string): Promise<any> {
		if (artifactType === ArtifactType.Recordings) {
			EventBus.$emit(
				"video-deleted",
				artifacts.map((a) => a.id)
			);
		}
		return await this.delete(`/bulk-actions/${artifactType}`, { artifacts, conversationId });
	},
	/**
	 * Adds or replaces tags on multiple artifacts in a single request.
	 * Mirrors the existing bulk-actions pattern used by deleteArtifacts.
	 * @param artifactType 'snapshots' | 'recordings' | 'files'
	 * @param artifacts Array of { id } or { id, conversationId } for recordings
	 * @param tags Array of Label objects to apply
	 * @param operation 'add' to append (default), or 'replace' to overwrite
	 */
	addTagsToArtifacts: async function (
		artifactType: string,
		artifacts: any[],
		tags: any[],
		operation: "add" | "replace" = "add"
	): Promise<any> {
		return await this.put(`/bulk-actions/${artifactType}/tags`, { artifacts, tags, operation });
	},
	deleteRecording: async function (conversationId: string, recordingId: string): Promise<any> {
		EventBus.$emit("video-deleted", [recordingId]);
		const r = await this._fetch(this.buildUrl(`/recordings/${conversationId}/${recordingId}`), {
			method: "DELETE",
			headers: {
				authorization: `Bearer ${await this.getToken()}`
			}
		});
		return this._handleResponse(r);
	},
	getRecordingShareUrl: async function ({
		meetingId,
		recordingId,
		conversationId
	}: {
		meetingId: string;
		recordingId: string;
		conversationId: string;
	}): Promise<any> {
		const response = await this._fetch(
			this.buildUrl(`/recordings/share/url?conversationId=${conversationId}&meetingId=${meetingId}&recordingId=${recordingId}`),
			{
				method: "GET",
				headers: { authorization: `Bearer ${await this.getToken()}` }
			}
		);
		return this._handleResponse(response);
	},
	getSnapshotShareUrl: async function ({ conversationId, snapshotId }: { conversationId: string; snapshotId: string }): Promise<any> {
		const response = await this._fetch(
			this.buildUrl(`/snapshots/share/url?conversationId=${conversationId}&snapshotId=${snapshotId}`),
			{
				method: "GET",
				headers: { authorization: `Bearer ${await this.getToken()}` }
			}
		);
		return this._handleResponse(response);
	},
	uploadSnapshot: async function (conversationId: string, blob: Blob, parentId: string | null, metadata: any): Promise<void> {
		const idempotencyId = metadata?.idempotencyId;
		const uploadId = idempotencyId ?? newUploadId();
		try {
			await getAdminSmallFileUploadLane(this as unknown as AdminSmallFileApi).enqueueAndWait({
				id: `admin-snapshot:${conversationId}:${uploadId}`,
				conversationId,
				blob,
				operation: parentId ? "annotation" : "snapshot",
				parentId,
				idempotencyId: uploadId,
				metadata
			});
		} catch (err) {
			logError("Failed to upload snapshot to s3", redactedUploadFailure(err, "Snapshot upload failed"), {
				method: "uploadSnapshot",
				file: "api/index.ts",
				conversationId,
				parentId,
				fileType: blob.type,
				...uploadFailureLogDetails(err)
			});
			throw err;
		}
	},
	updateSnapshotImage: async function (snapshotId: string, conversationId: string, blob: Blob) {
		const lane = getAdminSmallFileUploadLane(this as unknown as AdminSmallFileApi);
		const retryable = await lane.findRetryableRotation(conversationId, snapshotId);
		const idempotencyId = newUploadId();
		const result = await lane.enqueueAndWait({
			id: retryable?.id ?? `admin-rotation:${snapshotId}:${idempotencyId}`,
			conversationId,
			blob,
			operation: "rotation",
			snapshotId,
			idempotencyId
		});
		return { mediaUrl: result.mediaUrl ?? "" };
	},
	deleteOrganizationLogo: async function (organizationId: string): Promise<any> {
		const r = await this._fetch(
			this.buildUrl(`/organizations/${organizationId}/logo`),
			await this.buildHeaders({
				method: "DELETE"
			})
		);
		return this._handleResponse(r);
	},
	deleteOrganizationVirtualBackground: async function (organizationId: string): Promise<any> {
		const r = await this._fetch(
			this.buildUrl(`/organizations/${organizationId}/background`),
			await this.buildHeaders({
				method: "DELETE"
			})
		);
		return this._handleResponse(r);
	},
	getIndustries: async function (): Promise<any[]> {
		return this.get(`/industries`);
	},
	updateOrganizationLogo: async function (organizationId: string, file: File): Promise<any> {
		const uploaded = await getAdminSmallFileUploadLane(this as unknown as AdminSmallFileApi).enqueueOrganization({ organizationId, kind: "logo", blob: file });
		if (uploaded) return uploaded.organization;
		const formData = new FormData();

		formData.append("logo", file);
		const r = await this._fetch(this.buildUrl(`/organizations/${organizationId}/logo`), {
			method: "POST",
			body: formData,
			headers: {
				authorization: `Bearer ${await this.getToken()}`
			}
		});
		return this._handleResponse(r);
	},
	// The signed-in organization's public upload link (QR code). The first call creates it and seeds its recipients.
	getUploadLink: async function (): Promise<OrganizationUploadLink> {
		return this.get(`/upload-links/current`);
	},
	// Whether the organization has an upload link (the My Stuff hint). Read-only: never creates the link.
	getUploadLinkStatus: async function (): Promise<OrganizationUploadLinkStatus> {
		return this.get(`/upload-links/current/status`);
	},
	// Replaces the link's code: the old link and QR code stop working right away.
	regenerateUploadLink: async function (): Promise<OrganizationUploadLink> {
		return this.post(`/upload-links/current/regenerate`, {});
	},
	// LSP-21829: the Reviews page — every submitted customer review in the organization, paged and sorted on the server.
	getCustomerReviews: async function (query: CustomerReviewListQuery): Promise<CustomerReviewListResponse> {
		const params = new URLSearchParams({ page: String(query.page), pageSize: String(query.pageSize) });
		if (query.search) params.set("search", query.search);
		if (query.order) params.set("order", query.order);
		if (query.orderDirection) params.set("orderDirection", query.orderDirection);
		return this.get(`/customer-reviews?${params.toString()}`);
	},
	// LSP-21829: the Customer Review section of a session's About tab (rated reviews and requests still waiting on a rating).
	getSessionCustomerReviews: async function (conversationId: string): Promise<CustomerReviewCardResponse> {
		return this.get(`/customer-reviews/by-session/${encodeURIComponent(conversationId)}`);
	},
	// LSP-21829: the Customer Review section of a project's About tab.
	getProjectCustomerReviews: async function (projectId: string): Promise<CustomerReviewCardResponse> {
		return this.get(`/customer-reviews/by-project/${encodeURIComponent(projectId)}`);
	},
	updateOrganizationVirtualBackground: async function (organizationId: string, file: File): Promise<any> {
		const uploaded = await getAdminSmallFileUploadLane(this as unknown as AdminSmallFileApi).enqueueOrganization({ organizationId, kind: "virtual-background", blob: file });
		if (uploaded) return uploaded.organization;
		const formData = new FormData();

		formData.append("virtualBackground", file);
		const r = await this._fetch(this.buildUrl(`/organizations/${organizationId}/background`), {
			method: "POST",
			body: formData,
			headers: {
				authorization: `Bearer ${await this.getToken()}`
			}
		});
		return this._handleResponse(r);
	},
	getVirtualBackgrounds: async function (orgId: string): Promise<any> {
		return this.get(`/organizations/${orgId}/virtual-backgrounds`);
	},
	getSharingUsersForProject: async function (projectId: string): Promise<any> {
		const users: any[] = [];
		const seenUserIds = new Set<string>();
		for (let page = 1; ; page += 1) {
			const pageUsers = await this.get(`/users/project/${projectId}?page=${page}&pageSize=${PROJECT_SHARING_PAGE_SIZE}`);
			if (!Array.isArray(pageUsers) || pageUsers.length === 0) return users;
			let addedUsers = 0;
			for (const user of pageUsers) {
				const userId = user?.id == null ? null : String(user.id);
				if (userId && seenUserIds.has(userId)) continue;
				if (!userId) continue;
				seenUserIds.add(userId);
				users.push(user);
				addedUsers += 1;
			}
			if (addedUsers === 0) return users;
		}
	},
	getProjectAccessSummary: async function (projectId: string) {
		return this.get(`/users/project/${projectId}/access-summary`);
	},
	search: async function (searchRequest: SearchRequest, entitySearch = false, mediaSearch = false, isAuthenticated: boolean = false) {
		let url = `/search`;
		const query = "";

		if (entitySearch) {
			url = `/search/entity`;
		} else if (mediaSearch) {
			url = isAuthenticated ? `/search/media` : `/search/public/media`;
		}

		if (searchRequest) {
			const queryString = searchRequestToQueryString(searchRequest);
			url = `${url}?${queryString}`;
		}

		const ignoreAuth = mediaSearch && !isAuthenticated;
		// LSP-16314: media searches are polled heavily during uploads; skip the
		// _cacheBust query param so the preflight is cached (route sends no-store).
		const response = await this.get(url, ignoreAuth, mediaSearch);
		return response;
	},
	entitySearch: async function (searchRequest: SearchRequest) {
		return this.search(searchRequest, true);
	},
	mediaSearch: async function (searchRequest: SearchRequest, isAuthenticated: boolean) {
		return this.search(searchRequest, false, true, isAuthenticated);
	},
	getSharingUsers: async function (args: any, warnings: any, requestOptions?: RequestInit): Promise<any> {
		let url = `/users/sharing`;
		if (arguments.length > 0) {
			const wherejson = encodeURIComponent(JSON.stringify(args.where || {}));
			const orderjson = encodeURIComponent(JSON.stringify(args.order || []));
			const page = args.page || 1;
			const pageSize = args.pageSize || 10;
			url += `?page=${page}&pageSize=${pageSize}&where=${wherejson}&order=${orderjson}`;
		}
		const r = await this._fetch(this.buildUrl(url), await this.buildHeaders(requestOptions));
		return this._handleResponse(r, warnings || []);
	},
	meOrganizationIdMatchesToken: async function (): Promise<boolean> {
		const me = await this.getMe();
		let token;
		try {
			token = await this.getToken();
		} catch (error) {
			if (error instanceof UnauthenticatedError || isTransientAuthTokenError(error)) {
				return false;
			}
			throw error;
		}
		if (!token) {
			return false;
		}
		// parse the jwt
		const tokenPayload = jwtDecode(token);
		return me?.organization?.id == (tokenPayload as any).organization_id;
	},
	hasFeature: async function (featureName: string, requiresAuth: boolean = true): Promise<boolean> {
		// Avoid triggering auth/getMe on public pages when auth is required
		if (requiresAuth) {
			try {
				const isAuth = await (auth as any).isAuthenticated();
				if (!isAuth) {
					return false;
				}
			} catch (e) {
				return false;
			}
		}

		let me = null;
		try {
			me = await this.getMe();
		} catch (e) {
			// don't really care about the error, just means me is null
			console.error(e);
		}

		if (requiresAuth && !me) {
			return false;
		}

		const policyFlag = (window as any).env["VITE_" + featureName];
		if (policyFlag === "true" || Boolean((window as any).env[featureName])) {
			return true;
		}
		const feature = me?.organization?.OrganizationFeatures?.find((f: { featureName: string }) => {
			return f.featureName == featureName;
		});
		return !!feature;
	},
	isAdmin: async function (): Promise<boolean> {
		const me = await this.getMe();
		return me?.account?.roles?.some((role: any) => role.name.toLowerCase().includes("admin"));
	},
	getMe: async function (options?: RequestInit): Promise<any> {
		const abortPromise = this._abortPromise(options?.signal);
		if (this._me) {
			return abortPromise ? await Promise.race([Promise.resolve(this._me), abortPromise]) : this._me;
		}
		if (this._mePromise) {
			return abortPromise ? await Promise.race([this._mePromise, abortPromise]) : this._mePromise;
		}

		const includePermissions = true;
		const includePlugins = true;
		const includeSubscription = false;

		if (!(await (auth as any).isAuthenticated())) {
			throw new UnauthenticatedError("Not authenticated"); // Simulate the 401 that you'd get back normally but save the error to the server.
		}

		this._mePromise = (async () => {
			const r = await this._fetch(
				this.buildUrl(
					`/me?includePermissions=${includePermissions}&includePlugins=${includePlugins}&includeSubscription=${includeSubscription}`
				),
				await this.buildHeaders()
			);
			this._me = await this._handleResponse(r);
			this._setDatadogContext(this._me);
			timezones.setDateFormat((this._me as any)?.organization?.dateFormat);
			return this._me;
		})().finally(() => {
			this._mePromise = null;
		});

		return abortPromise ? await Promise.race([this._mePromise, abortPromise]) : this._mePromise;
	},
	/**
	 * LSP-17619 — write the zone a booking just persisted straight into the cached /me.
	 *
	 * The server's preference write is deliberately fire-and-forget from an afterCommit hook, so
	 * evicting the cache and re-fetching RACES that UPDATE: a fetch that wins reads the OLD zone, and
	 * because getMe() short-circuits on a non-null `_me`, that stale value is then served for the rest
	 * of the session rather than self-correcting. Patching avoids the race entirely — the client knows
	 * exactly which zone it submitted, and the server stores that same canonical value.
	 *
	 * A no-op when nothing is cached: there is no stale value to correct, and the next fetch is
	 * already ordered after the booking response.
	 */
	setCachedSchedulingTimezone: function (timeZone: string): void {
		const cachedUser = (this._me as any)?.user;
		if (!cachedUser || !timeZone) {
			return;
		}
		cachedUser.schedulingTimezone = timeZone;
	},
	invalidateMe: function (): void {
		// Clear the /users/me cache without refetching. The next consumer that
		// calls getMe() will trigger the fresh fetch, so we don't pay for it
		// unless someone is actually about to use the data.
		this._me = null;
		this._mePromise = null;
	},
	refreshMe: async function (customHeaders?: RequestInit): Promise<any> {
		this._me = null;
		this._mePromise = null;
		const includePermissions = true;
		const includePlugins = true;
		const includeSubscription = false;

		const r = await this._fetch(
			this.buildUrl(
				`/me?includePermissions=${includePermissions}&includePlugins=${includePlugins}&includeSubscription=${includeSubscription}`
			),
			await this.buildHeaders(customHeaders as any)
		);

		this._me = await this._handleResponse(r);
		this._setDatadogContext(this._me);
		timezones.setDateFormat((this._me as any)?.organization?.dateFormat);
		return this._me;
	},
	createBrowserIdToken: async function (browserId: string): Promise<any> {
		return this.post(`/tokens/token`, { browserId: browserId });
	},
	submitSupportRequest: async function (message: string, topic: "help" | "accounts"): Promise<any> {
		return this.post(`/me/support`, { message: message, topic: topic });
	},
	submitReferral: async function (payload: {
		firstName: string;
		lastName: string;
		email: string;
		companyName: string;
	}): Promise<{ success: boolean }> {
		return this.post(`/me/refer-a-friend`, payload);
	},
	clearAllNotifications: async function (): Promise<any> {
		return this.post(`/notifications/clearAll`, {});
	},
	// Bulk-delete the caller's own notifications. The API scopes every delete to the requesting user,
	// so ids belonging to another user are ignored. Returns { deletedCount }.
	bulkDeleteNotifications: async function (ids: string[]): Promise<{ deletedCount: number }> {
		return this.post(`/notifications/bulk-delete`, { ids });
	},
	getNotificationsUnreadCount: async function (): Promise<{ count: number }> {
		return this.get(`/notifications/unread-count`);
	},
	createShortenedUrl: async function (url: string, expiration = 30): Promise<{ url: string }> {
		const r = await this._fetch(
			this.buildUrl(`/s?expiration=` + expiration),
			await this.buildHeaders({
				method: "POST",
				body: JSON.stringify({ url })
			})
		);
		return this._handleResponse(r);
	},
	getBillingPortalSessionUrl: async function (flow: string, returnUrl: string): Promise<any> {
		const r = await this._fetch(
			this.buildUrl(`/subscriptions/billing/link`),
			await this.buildHeaders({
				method: "POST",
				body: JSON.stringify({
					flow: flow,
					returnUrl: returnUrl
				})
			})
		);
		return this._handleResponse(r);
	},
	initiateMultipartUpload: async function (
		conversationId: string,
		fileDetails: FileDetails[],
		type: string,
		totalChunks: number,
		projectChecklistItemId?: string
	) {
		const response = await this._fetch(
			this.buildUrl("/files/multipart/initiate"),
			await this.buildHeaders({
				method: "POST",
				body: JSON.stringify({
					conversationId,
					fileDetails,
					type,
					totalChunks,
					...(totalChunks > 1_000 && { multipartPartCapacity: 10_000 }),
					projectChecklistItemId
				})
			})
		);
		return this._handleResponse(response);
	},
	initiateRecordingMultipartUpload: async function (
		conversationId: string,
		sessionClientId: string,
		recordingClientId: string,
		fileType: string,
		totalChunks: number,
		projectChecklistItemId?: string
	) {
		const response = await this._fetch(
			this.buildUrl("/recordings/multipart/initiate"),
			await this.buildHeaders({
				method: "POST",
				body: JSON.stringify({
					conversationId,
					sessionClientId,
					recordingClientId,
					fileType,
					totalChunks,
					...(totalChunks > 1_000 && { multipartPartCapacity: 10_000 }),
					projectChecklistItemId
				})
			})
		);
		return this._handleResponse(response);
	},
	_uploadsClient: null as UploadsFacadeClient | null,
	/**
	 * Admin's `/uploads` wire is the shared `UploadsFacadeClient`; only admin's *policy* stays
	 * local (D3/D6: share the wire, inject the policy). The shared client owns the URL shapes,
	 * the error-code extraction, and the empty-body tolerance; admin injects its transport
	 * (`_fetch` + `buildUrl` + `buildHeaders`) and its app-wide rejection policy
	 * (`_handleResponse`), so callers keep the exact `{code, message, isJson, httpStatus, url}`
	 * error object and every side effect that hangs off it.
	 */
	_uploadsFacade: function (): UploadsFacadeClient {
		if (!this._uploadsClient) {
			this._uploadsClient = new UploadsFacadeClient({
				// Empty base: the shared client only concatenates, while admin's URL policy — API
				// root, base path, and the `_cacheBust` param every admin request carries — lives in
				// buildUrl. The path arrives here unqualified and buildUrl finishes it, which also
				// keeps the API root resolved per request rather than frozen at construction.
				baseUrl: "",
				fetcher: async (input: RequestInfo | URL, init?: RequestInit) => {
					if (init?.signal?.aborted) throw abortError();
					// The shared client passes a `Headers` instance; `buildHeaders` spreads its argument
					// into a plain record. Flatten to that record here so buildHeaders can see (and
					// override) the individual header names — spreading a `Headers` instance yields no
					// own enumerable properties, so every header the shared client set would be dropped.
					// (x-request-id itself is safe either way: `_fetch` normalizes through the `Headers`
					// API before calling fetch, so it survives whichever container it is handed.)
					const headers: Record<string, string> = {};
					for (const [name, value] of new Headers(init?.headers)) {
						// buildHeaders sets Content-Type itself. Header names are case-insensitive and the
						// iteration casing is not guaranteed, so skip it case-insensitively — two differently
						// cased keys in the same record become one duplicated header on the wire.
						if (name.toLowerCase() === "content-type") continue;
						headers[name] = value;
					}
					const request = await this.buildHeaders({ ...init, headers });
					if (init?.signal?.aborted) throw abortError();
					return this._fetch(this.buildUrl(String(input)), request);
				},
				onResponseError: (response: Response) => this._handleResponse(response) as Promise<never>
			});
		}
		return this._uploadsClient;
	},
	createUploadsUpload: async function (body: Record<string, unknown>, signal?: AbortSignal) {
		// Callers (and the AdminSmallFileApi port) build these bodies as open records, so the
		// narrowing to the shared request type happens here rather than at every call site.
		return this._uploadsFacade().create(body as unknown as UploadsCreateRequest, signal);
	},
	// Reserve at pick (docs/plans/2026-10-16-web-lazy-multipart-initiate.md): the create body + reserveOnly.
	reserveUploadsUpload: async function (body: Record<string, unknown>, signal?: AbortSignal) {
		return this._uploadsFacade().reserve(body as unknown as UploadsCreateRequest, signal);
	},
	getUploadsUpload: async function (registryId: string) {
		return this._uploadsFacade().get(registryId);
	},
	getUploadsObjectStatus: async function (registryId: string, attemptId: string, generation: number) {
		return this._uploadsFacade().objectStatus(registryId, attemptId, generation);
	},
	refreshUploadsUrls: async function (
		registryId: string,
		attemptId: string,
		generation: number,
		partNumbers: number[],
		useRegionalEndpoint = false
	) {
		return this._uploadsFacade().urls(registryId, attemptId, generation, partNumbers, useRegionalEndpoint);
	},
	listUploadsParts: async function (registryId: string, attemptId: string, generation: number) {
		return this._uploadsFacade().parts(registryId, attemptId, generation);
	},
	postUploadsProgress: async function (
		registryId: string,
		attemptId: string,
		generation: number,
		progress: { receivedParts?: number; receivedBytes?: number; receivedMaxChunkIndex?: number },
		signal?: AbortSignal
	) {
		return this._uploadsFacade().progress(registryId, { ...progress, attemptId, generation }, signal);
	},
	// `parts` is OPTIONAL (LSP-17653 plan 3a): a chunked-object attempt completes with the attempt
	// FENCE only — the server re-derives the manifest from the landed `.part` objects and refuses a
	// body that carries `parts` at all (OPERATION_NOT_SUPPORTED). Omitting the argument must therefore
	// leave the key off the body entirely, not send `parts: undefined`.
	completeUploadsUpload: async function (registryId: string, attemptId: string, generation: number, parts?: unknown[]) {
		// Spread on `!== undefined`, not truthiness: an explicitly passed EMPTY parts array is a
		// multipart caller's own (server-rejected) claim, and swallowing it here would turn that
		// mistake into a silent chunked-object-shaped fence-only completion.
		return this._uploadsFacade().complete(registryId, { attemptId, generation, ...(parts !== undefined ? { parts } : {}) });
	},
	abortUploadsUpload: async function (
		registryId: string,
		attemptId: string,
		generation: number,
		transport: UploadsFacadeTransport,
		takeGeneration?: string,
		preserveOwner?: boolean,
 contractVersion?: 2
	) {
		return this._uploadsFacade().abort(registryId, {
			attemptId,
			generation,
			transport,
			...(takeGeneration ? { takeGeneration } : {}),
 ...(contractVersion === 2 ? { contractVersion } : {}),
			...(preserveOwner ? { preserveOwner: true } : {})
		});
	},
	completeMultipartUpload: async function (
		uploadId: string,
		s3Key: string,
		bucket: string,
		parts: { ETag: string; PartNumber: number }[]
	) {
		const response = await this._fetch(
			this.buildUrl("/files/multipart/complete"),
			await this.buildHeaders({
				method: "POST",
				body: JSON.stringify({
					uploadId,
					s3Key,
					bucket,
					parts,
					...(parts.some((part) => part.PartNumber > 1_000) && { multipartPartCapacity: 10_000 })
				})
			})
		);
		return this._handleResponse(response);
	},
	// `preserveRecordingSession` rides in a trailing options bag rather than a 5th positional boolean because
	// admin's abort already owns a positional `cancelled` flag that contact-async's endpoint does not have —
	// the two flags are independent (cancelled tombstones the pending file stub; preserveRecordingSession keeps
	// the pending recording session alive for an imminent retry) and the server reads both from the body.
	abortMultipartUpload: async function (
		uploadId: string,
		s3Key: string,
		bucket: string,
		cancelled = false,
		options?: { preserveRecordingSession?: boolean }
	) {
		const response = await this._fetch(
			this.buildUrl(`/files/multipart/${encodeURIComponent(uploadId)}/abort`),
			await this.buildHeaders({
				method: "POST",
				body: JSON.stringify(
					options?.preserveRecordingSession === undefined
						? { s3Key, bucket, cancelled }
						: { s3Key, bucket, cancelled, preserveRecordingSession: options.preserveRecordingSession }
				)
			})
		);
		return this._handleResponse(response);
	},
	refreshMultipartUrls: async function (uploadId: string, s3Key: string, bucket: string, partNumbers: number[]) {
		const response = await this._fetch(
			this.buildUrl(`/files/multipart/${encodeURIComponent(uploadId)}/refresh-urls`),
			await this.buildHeaders({
				method: "POST",
				body: JSON.stringify({
					s3Key,
					bucket,
					partNumbers,
					...(partNumbers.some((partNumber) => partNumber > 1_000) && { multipartPartCapacity: 10_000 })
				})
			})
		);
		return this._handleResponse(response);
	},
	listMultipartParts: async function (
		uploadId: string,
		s3Key: string,
		bucket: string
		// LSP-17796: `anomalous: true` means the server answered but could not vouch for the listing. It is
		// NOT a verified-empty manifest — see listPartsEvidenceFromResponse.
	): Promise<{ parts: { partNumber: number; etag: string; size: number }[]; gone?: boolean; anomalous?: boolean }> {
		const response = await this._fetch(
			this.buildUrl(`/files/multipart/${encodeURIComponent(uploadId)}/list-parts`),
			await this.buildHeaders({
				method: "POST",
				body: JSON.stringify({ s3Key, bucket })
			})
		);
		return this._handleResponse(response);
	},
	updateInstructionField: async function (instructionId: string, fields: Record<string, unknown>) {
		const url = `/instructions/${instructionId}/field`;
		const response = await this._fetch(
			this.buildUrl(url),
			await this.buildHeaders({
				method: "POST",
				body: JSON.stringify(fields)
			})
		);
		return this._handleResponse(response);
	},
	// LSP-20840: point the org's single default Guided Instructions template at `instructionId`,
	// or clear it with null. The API demotes the previous default in the same request.
	setDefaultInstruction: async function (instructionId: string | null) {
		const response = await this._fetch(
			this.buildUrl("/instructions/default"),
			await this.buildHeaders({
				method: "PUT",
				body: JSON.stringify({ instructionId })
			})
		);
		return this._handleResponse(response);
	},
	updateLiveCallFormField: async function (liveCallFormId: string, fields: Record<string, unknown>) {
		const url = `/live-call-forms/${liveCallFormId}/field`;
		const response = await this._fetch(
			this.buildUrl(url),
			await this.buildHeaders({
				method: "POST",
				body: JSON.stringify(fields)
			})
		);
		return this._handleResponse(response);
	},
	updateChecklistField: async function (checklistId: string, fields: Record<string, unknown>) {
		const url = `/checklists/${checklistId}/field`;
		const response = await this._fetch(
			this.buildUrl(url),
			await this.buildHeaders({
				method: "POST",
				body: JSON.stringify(fields)
			})
		);
		return this._handleResponse(response);
	},
	// LSP-15498: how many live projects use this checklist template, for the delete confirmation.
	getChecklistUsage: async function (checklistId: string): Promise<{ projectCount: number }> {
		const response = await this._fetch(this.buildUrl(`/checklists/${checklistId}/usage`), await this.buildHeaders());
		return this._handleResponse(response);
	},
	addChecklistItems: async function (checklistId: string, items: Partial<ChecklistItem>[]) {
		const url = `/checklists/${checklistId}/items`;
		const response = await this._fetch(
			this.buildUrl(url),
			await this.buildHeaders({
				method: "POST",
				body: JSON.stringify({ items })
			})
		);
		return this._handleResponse(response);
	},
	getProjectChecklists: async function (projectId: string, ignoreAuth: boolean = false) {
		const url = `/projects/${projectId}/checklists`;
		const response = await this._fetch(
			this.buildUrl(url),
			await this.buildHeaders(
				{
					method: "GET"
				},
				ignoreAuth
			)
		);
		return this._handleResponse(response);
	},
	createProjectChecklist: async function (projectId: string, checklistId: string) {
		const url = `/projects/${projectId}/checklists`;
		const response = await this._fetch(
			this.buildUrl(url),
			await this.buildHeaders({
				method: "POST",
				body: JSON.stringify({ checklistId: checklistId })
			})
		);
		return this._handleResponse(response);
	},
	deleteProjectChecklist: async function (projectId: string, checklistId: string) {
		const url = `/projects/${projectId}/checklists/${checklistId}`;
		const response = await this._fetch(
			this.buildUrl(url),
			await this.buildHeaders({
				method: "DELETE"
			})
		);
		return this._handleResponse(response);
	},
	updateProjectChecklistItem: async function (projectId: string, checklistId: string, checklistItem: ProjectChecklistItemUpdate) {
		const url = `/projects/${projectId}/checklists/${checklistId}/items/${checklistItem.checklistItemId}`;
		const requestBody: UpdateProjectChecklistItemRequest = {
			completed: checklistItem.completed,
			notes: checklistItem.notes
		};
		const response = await this._fetch(
			this.buildUrl(url),
			await this.buildHeaders({
				method: "PUT",
				body: JSON.stringify(requestBody)
			})
		);
		return this._handleResponse(response);
	},
	reorderProjectChecklistItems: async function (projectId: string, checklistId: string, items: { id: string }[]) {
		return this.post(`/projects/${projectId}/checklists/${checklistId}/items/reorder`, { items });
	},
	uploadProjectChecklistItemMedia: async function (
		projectId: string,
		checklistId: string,
		checklistItemId: string,
		mediaItems: { mediaId: string; mediaType: string }[]
	) {
		const url = `/projects/${projectId}/checklists/${checklistId}/items/${checklistItemId}/media`;
		const response = await this._fetch(
			this.buildUrl(url),
			await this.buildHeaders({
				method: "POST",
				body: JSON.stringify({ mediaItems: mediaItems })
			})
		);
		return this._handleResponse(response);
	},
	deleteProjectChecklistItemMedia: async function (projectId: string, checklistId: string, checklistItemId: string, mediaId: string) {
		const url = `/projects/${projectId}/checklists/${checklistId}/items/${checklistItemId}/media/${mediaId}`;
		const response = await this._fetch(
			this.buildUrl(url),
			await this.buildHeaders({
				method: "DELETE"
			})
		);
		return this._handleResponse(response);
	},
	getUsedAvatarColors: async function () {
		const url = `/accounts/avatar-colors/used`;
		const r = await this._fetch(this.buildUrl(url), await this.buildHeaders());
		const response = await this._handleResponse(r);
		// LSP-14756: the API returns a bare array. The `{ colors }` branch is NOT dead code — it is
		// the rollback path. The deploy order is API first then admin, so the forward gap never needs
		// it, but an API rollback (or admin shipping ahead of the API) puts the wrapper back on the
		// wire, and this branch is what keeps that a no-op rather than a silent behaviour change.
		//
		// Why it has to be handled rather than left to fail: this reader returns `[]` without
		// throwing, so the catch/console.error at UserPage.vue:521 never fires. getDefaultColor()
		// then filters the palette against an empty list and returns the FIRST palette colour for
		// every user it defaults, instead of the first unused one — duplicate avatar colours, with
		// nothing logged anywhere.
		//
		// Safe to delete once the array response has been live in production for one full release.
		return Array.isArray(response) ? response : response?.colors || [];
	},
	// LSP-15861: persists the "Don't show again" state for the Podium install banner on the
	// CURRENT-ORG account (not the identity-global user), so the dismissal/acknowledgement —
	// and the reconnect reset — apply only to the org they were made in.
	// expectedAccountId (review H2): the account id captured when the caller QUEUED this write.
	// buildHeaders() awaits auth.getAccessToken() below — if the org switches during that await,
	// the request authenticates as the NEW org and this PUT would otherwise mutate its account
	// with a value intended for the old one. The server rejects when this doesn't match the
	// account it actually authenticated as. Optional so callers that don't pass it keep working.
	updateAccountBannerState: async function (state: string | null, expectedAccountId?: string) {
		const r = await this._fetch(
			this.buildUrl(`/accounts/self`),
			await this.buildHeaders({
				method: "PUT",
				body: JSON.stringify(
					expectedAccountId === undefined
						? { pluginInstallBannerState: state }
						: { pluginInstallBannerState: state, expectedAccountId }
				)
			})
		);
		return this._handleResponse(r);
	},
	// LSP-21826: reports that the user closed the "Open / Get the LiveSwitch app" home-page banner
	// ("dismissed"; taps are not reported). The API owns the snooze ladder and returns the updated account.
	// expectedAccountId guards the same mid-flight org-switch race as updateAccountBannerState.
	// keepalive: a user who taps × and leaves the page at once still has the snooze saved.
	recordMobileAppBannerEvent: async function (event: MobileAppBannerEvent, expectedAccountId?: string) {
		const r = await this._fetch(
			this.buildUrl(`/accounts/self/mobile-app-banner`),
			await this.buildHeaders({
				method: "PUT",
				keepalive: true,
				body: JSON.stringify(expectedAccountId === undefined ? { event } : { event, expectedAccountId })
			})
		);
		return this._handleResponse(r);
	},
	buildUrl: function (path: string, query?: Record<string, any>, skipCacheBust: boolean = false) {
		if (path.indexOf("/") !== 0) {
			path = "/" + path;
		}
		let domain = (window as any).env.VITE_API_ROOT;
		if (domain.endsWith("/")) {
			domain = domain.substring(0, domain.length - 1);
		}
		const params = [];
		if (query) {
			for (const q in query) {
				if (query.hasOwnProperty(q)) {
					const v = query[q];
					params.push(encodeURIComponent(q) + "=" + encodeURIComponent(v));
				}
			}
		}
		// LSP-16314: poll endpoints opt out of cache-busting so their URL is stable
		// and the browser can reuse the cached CORS preflight (those routes send
		// `Cache-Control: no-store`, so the GET response stays fresh without it).
		if (!skipCacheBust) {
			params.push("_cacheBust=" + new Date().getTime());
		}
		if (params.length > 0) {
			const separator = path.indexOf("?") === -1 ? "?" : "&";
			path += separator + params.join("&");
		}
		const final = domain + this._basePath + path;
		return final;
	},
	buildHeaders: async function (obj?: Record<string, any>, ignoreAuth: boolean = false) {
		const headers = {
			...obj,
			headers: {
				"Content-Type": "application/json",
				...(obj?.headers || {})
			}
		};

		if (!ignoreAuth) {
			let token;
			try {
				token = await (auth as any).getAccessToken();
			} catch (error) {
				if (isTransientAuthTokenError(error)) {
					logWarn("api.buildHeaders: transient auth token failure; request deferred before fetch", {
						classification: "transient",
						retryable: true
					});
				}
				throw error;
			}
			if (!token) {
				throw new UnauthenticatedError("Unable to build authenticated request headers without an access token.");
			}
			headers.headers.authorization = `Bearer ${token}`;
		}

		return headers;
	},
	_handleResponse: async function (r: Response, warnings?: { code: string; description: string }[]): Promise<any> {
		if (r.ok) {
			// this occurs for 200-299 error codes
			const json = await r.json();
			if (r.headers.has("Warning") && warnings) {
				const parsedWarnings = parseWarningHeader(r.headers.get("Warning"));
				for (const parsedWarning of parsedWarnings) {
					warnings.push(parsedWarning);
				}
			}
			return json;
		} else {
			let t = null;
			const contentType = r?.headers?.get("Content-Type"); // Case insenstive.
			if (contentType && contentType.indexOf("application/json") > -1) {
				try {
					t = await r.json();
					t.code ??= r.status;
					t.isJson = true;
					t.httpStatus = r.status;
				} catch (error) {
					logWarn("api._handleResponse: invalid JSON error body", { httpStatus: r.status, ...uploadFailureLogDetails(error) });
					t = { code: r.status, message: "", isJson: false, httpStatus: r.status };
				}
			} else {
				t = {
					code: r.status,
					message: await r.text(),
					isJson: false,
					httpStatus: r.status
				};
			}
			const retryAfter = r.headers.get("Retry-After");
			if (retryAfter !== null) t.retryAfterHeader = retryAfter;
			if (typeof t.message === "string" && t.message.includes("Organization id does not match the current context")) {
				if (canReloadForOrgSwitch()) {
					window.location.reload();
					return new Promise(() => {});
				}
				if (isReloadPending()) {
					return new Promise(() => {});
				}
			}
			const noAccountMessage = t?.message ?? t?.error;
			if (t?.errorCode) {
				switch (t.errorCode) {
					case "USER_NOT_FOUND":
						this.openNoAccountDialog(undefined, noAccountMessage);
						break;
					case "ACCOUNT_NOT_FOUND":
					case "ORGANIZATION_NOT_FOUND":
						this.openNoAccountDialog("org_access_lost", noAccountMessage);
						break;
					case "INVALID_TOKEN":
					case "TOKEN_INVALID":
					case "UNAUTHORIZED":
					case "TOKEN_EXPIRED":
						// AUTOMATIC (review 3857165286, HIGH): this is a background API response, not a
						// user asking to sign in. Left unclassified it fired loginWithRedirect into a
						// committing bfcache-restore recovery navigation and could drop a live session
						// on Auth0's login screen.
						await auth.doDefaultLogin({ isExplicitUserIntent: false });
						break;
					default:
						// Because this handles more endpoints than just the me endpoint, we don't want to log them out if it fails for any other reason.
						break;
				}
			}
			// Log the path only (query params can carry autologin credentials) and scrub any
			// claim token riding in the path itself (/share/:token, /share-claims/:token) —
			// this console.error is forwarded to Datadog Logs.
			const loggedUrl = scrubClaimToken(r.url.split("?")[0]);
			if (isUploadProgressConflict(t)) {
				// LSP-19605: the uploads facade's lifecycle gate refused a progress heartbeat — usually
				// because `/complete` had already begun. The heartbeat is never the authority on the
				// session's fate (the complete/urls calls are), so this is not an HTTP error of the client's.
				// console.info is forwarded to Datadog once by `plugins/datadog.ts` (`forwardConsoleLogs`
				// includes "info"; the `logInfo` helper would ingest it twice), so the rate stays countable
				// without inflating the error budget. The rejection below is unchanged.
				console.info("[Upload] Progress report refused by the server's lifecycle gate (no longer accepting progress)", {
					httpStatus: t.code,
					url: loggedUrl
				});
			} else if (t?.errorCode === "UPLOAD_TRANSPORT_UNSUPPORTED") {
				// LSP-19929: the small-file lane offers direct-put first and treats this 400 as "use
				// multipart" (see adminSmallFileUploadLane.presignRecord). It is transport negotiation, not
				// a failure, so it must not paint the console red or count against the Datadog error budget.
				console.info("[Upload] Server declined the offered upload transport; the caller falls back to multipart", {
					httpStatus: t.code,
					url: loggedUrl
				});
			} else {
				console.error(`Http Error: StatusCode=${t.code}, Url=${loggedUrl}`);
			}
			t.url = r.url;
			this.fire("error", t);
			return Promise.reject(t);
		}
	}
};

const entities = [
	{
		singular: "Account",
		plural: "Accounts",
		path: "/accounts"
	},
	{
		singular: "Appointment",
		plural: "Appointments",
		path: "/appointments"
	},
	{
		singular: "User",
		plural: "Users",
		path: "/users"
	},
	{
		singular: "Organization",
		plural: "Organizations",
		path: "/organizations"
	},
	{
		singular: "Conversation",
		plural: "Conversations",
		path: "/conversations"
	},
	{
		singular: "Project",
		plural: "Projects",
		path: "/projects"
	},
	{
		singular: "ProjectShare",
		plural: "ProjectShares",
		path: "/project-share"
	},
	{
		singular: "ConversationView",
		plural: "ConversationViews",
		path: "/conversation-views"
	},
	{
		singular: "ConversationViewSearch",
		plural: "ConversationViewsSearch",
		path: "/conversation-views/search"
	},
	{
		// LSP-16642: compact base-table list for the conversations table (no search/filter applied).
		// Returns the same row shape as ConversationViews, sourced from /conversations/v2.
		singular: "ConversationV2",
		plural: "ConversationsV2",
		path: "/conversations/v2"
	},
	{
		singular: "Customer",
		plural: "Customers",
		path: "/customers"
	},
	{
		singular: "CustomerView",
		plural: "CustomerViews",
		path: "/customer-views"
	},
	{
		singular: "CustomerViewSearch",
		plural: "CustomerViewsSearch",
		path: "/customer-views/search"
	},
	{
		singular: "ProjectShareView",
		plural: "ProjectShareViews",
		path: "/project-share-views"
	},
	{
		singular: "ProjectShareViewSearch",
		plural: "ProjectShareViewsSearch",
		path: "/project-share-views/search"
	},
	{
		singular: "Form",
		plural: "Forms",
		path: "/forms"
	},
	{
		singular: "Plugin",
		plural: "Plugins",
		path: "/plugins"
	},
	{
		singular: "PluginInstallation",
		plural: "PluginInstallations",
		path: "/plugin-installations"
	},
	{
		singular: "AsyncForm",
		plural: "AsyncForms",
		path: "/async-forms"
	},
	{
		singular: "Snapshot",
		plural: "Snapshots",
		path: "/snapshots"
	},
	{
		singular: "Subscription",
		plural: "Subscriptions",
		path: "/subscriptions"
	},
	{
		singular: "Label",
		plural: "Labels",
		path: "/labels"
	},
	{
		singular: "EntityAttribute",
		plural: "EntityAttributes",
		path: "/entity-attributes"
	},
	{
		singular: "Attribute",
		plural: "Attributes",
		path: "/attributes"
	},
	{
		singular: "AttributeGroup",
		plural: "AttributeGroups",
		path: "/attribute-groups"
	},
	{
		singular: "File",
		plural: "Files",
		path: "/files"
	},
	{
		singular: "Comment",
		plural: "Comments",
		path: "/comments"
	},
	{
		singular: "Notification",
		plural: "Notifications",
		path: "/notifications"
	},
	{
		singular: "Checklist",
		plural: "Checklists",
		path: "/checklists"
	},
	{
		singular: "Instruction",
		plural: "Instructions",
		path: "/instructions"
	},
	{
		singular: "LiveCallForm",
		plural: "LiveCallForms",
		path: "/live-call-forms"
	}
];
for (let i = 0; i < entities.length; i++) {
	const entity = entities[i];
	(api as any)["get" + entity.plural] = async function (args: any, warnings: any, customHeaders: any) {
		let url = `${entity.path}`;
		if (args && args.urlModifier) {
			url = args.urlModifier(url);
		}
		if (arguments.length > 0) {
			const wherejson = encodeURIComponent(JSON.stringify(args.where || {}));
			const orderjson = encodeURIComponent(JSON.stringify(args.order || []));
			const page = args.page || 1;
			const pageSize =
				entity.plural !== "Forms"
					? args.pageSize || 10
					: normalizePageSizeToSupportedOption(args.pageSize, WEB_FORM_PAGE_SIZES, 10);
			url += `?page=${page}&pageSize=${pageSize}&where=${wherejson}&order=${orderjson}`;

			if ((entity.plural === "ProjectShareViews" || entity.plural === "ProjectShareViewsSearch") && args?.includeThumbnails) {
				url += `&includeThumbnails=true`;
			}

			if (args.searchRequest) {
				const nestedSearchRequest = { ...args.searchRequest };
				delete nestedSearchRequest.page;
				delete nestedSearchRequest.pageSize;
				const searchQuery = searchRequestToQueryString(nestedSearchRequest);
				url += `&${searchQuery}`;
			}
		}

		const r = await this._fetch(this.buildUrl(url), await this.buildHeaders(customHeaders));
		return this._handleResponse(r, warnings || []);
	};
	// create/update/delete route through `_fetchWithRetry` (not raw `_fetch`) so the
	// generic entity factory gets the same 503/425 Idempotency-Key retry as the
	// `post`/`put`/`delete` helpers. This is what LSP-15857 is actually fixing —
	// `createConversation` (the exact ActionVideo.start incident path) is generated
	// here, not through `post()`. Safe for every entity not in
	// ENTITIES_WITH_SERVER_BODY_DERIVED_IDEMPOTENCY_KEY: the retry only fires when
	// the response itself carries the server's Idempotency-Stored/Replayed contract
	// header (see `isProtectedByIdempotency`), so an entity with no server-side
	// idempotent config just falls through with no retry, same as before, plus the
	// new logging. Entities WITH a body-derived key skip this and stay on raw
	// `_fetch` (see the constant's definition above for why).
	const usesServerBodyDerivedKey = ENTITIES_WITH_SERVER_BODY_DERIVED_IDEMPOTENCY_KEY.has(entity.singular);
	const mutate = usesServerBodyDerivedKey ? "_fetch" : "_fetchWithRetry";
	(api as any)["create" + entity.singular] = async function (body: any, warnings: any) {
		delete body.id;
		const r = await this[mutate](
			this.buildUrl(`${entity.path}/`),
			await this.buildHeaders({
				method: "POST",
				body: JSON.stringify(body)
			})
		);
		return this._handleResponse(r, warnings || []);
	};
	(api as any)["get" + entity.singular] = async function (id: string, warnings: any) {
		const r = await this._fetch(this.buildUrl(`${entity.path}/${id}`), await this.buildHeaders());
		return this._handleResponse(r, warnings || []);
	};
	(api as any)["delete" + entity.singular] = async function (id: string, query: any, warnings: any) {
		const r = await this[mutate](
			this.buildUrl(`${entity.path}/${id}`, query),
			await this.buildHeaders({
				method: "DELETE"
			})
		);
		return this._handleResponse(r, warnings || []);
	};
	(api as any)["update" + entity.singular] = async function (id: string, body: any, warnings: any) {
		const { id: _omitId, ...bodyWithoutId } = body ?? {};
		const r = await this[mutate](
			this.buildUrl(`${entity.path}/${id}`),
			await this.buildHeaders({
				method: "PUT",
				body: JSON.stringify(bodyWithoutId)
			})
		);
		return this._handleResponse(r, warnings || []);
	};
}

(api as any).updateForm = async function (id: string, body: any, query?: Record<string, any>, warnings?: any) {
	delete body.id;
	const r = await this._fetchWithRetry(
		this.buildUrl(`/forms/${id}`, query),
		await this.buildHeaders({
			method: "PUT",
			body: JSON.stringify(body)
		})
	);
	return this._handleResponse(r, warnings || []);
};

export default api as unknown as ApiInterface;
